import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { forwardRef, useId, useRef, useImperativeHandle, useEffect, useState, useCallback, useMemo, createContext, useLayoutEffect, useContext, Children, isValidElement, cloneElement } from 'react';
import { Tooltip } from '@norkron/web-tooltip';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { registerResponsiveContract, checkInteractiveElementVisuallyEmpty, checkContrast, checkViewportOverflow, checkHeadingHierarchy, checkDuplicateVisibleText, checkTextClipped, checkInlineStyleOnLibStamp, checkUtilityClassOnLibStamp } from '@norkron/web-conformance';

// src/error-codes.ts
var WEB_CONTROLS_LIB = "@norkron/web-controls";
var WEB_CONTROLS_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-controls");
var { warn, panic, reportInvalidInput } = createScopedErrors(
  WEB_CONTROLS_LIB,
  WEB_CONTROLS_TOKEN
);
try {
  registerCodes(WEB_CONTROLS_LIB, {
    WEB_CONTROLS_INVALID_INPUT: {
      description: "A consumer passed a prop that violates the component's Zod input schema (wrong type, out-of-range size, invalid variant, missing required slot, etc.). The control degrades to a sane default; the warning carries the rejected value and the Zod issue path so the consumer can locate the call site.",
      severity: "warning",
      docsAnchor: "web-controls/invalid-input",
      exampleFix: "Read the offending prop name + path from the extras.zodIssue. Pass a value matching the schema in the lib types."
    },
    WEB_CONTROLS_ICON_BUTTON_NO_LABEL: {
      description: 'An IconButton was rendered with no accessible name \u2014 neither `aria-label`, `aria-labelledby`, nor a visible text child. Screen-reader users hear "button" with no context. Critical a11y regression.',
      severity: "error",
      docsAnchor: "web-controls/icon-button-no-label",
      exampleFix: 'Pass `aria-label="<intent>"` to IconButton, OR pass `aria-labelledby=<id of a visible label>`. The text label is mandatory; do not work around with `data-allow-empty`.'
    },
    WEB_CONTROLS_ICON_FONT_GLYPH: {
      description: 'A control received an icon slot that is a className-based glyph (`<i class="nf-icn">`, `<span class="fa fa-check">`, etc.) instead of an inline `<svg>` or React node. Icon-font glyphs fail silently when the font does not load (STR_INTERACTIVE_ELEMENT_VISUALLY_EMPTY). The lib renders the node but warns the consumer to migrate.',
      severity: "warning",
      docsAnchor: "web-controls/icon-font-glyph",
      exampleFix: 'Replace the `<i className="nf-icn ...">` with an inline `<svg>` (preferred) or a React component that ships its own visual content (not class-based glyphs).'
    },
    WEB_CONTROLS_CONTROLLED_VS_DEFAULT: {
      description: "A control received BOTH a controlled prop (`checked`, `value`, etc.) AND its `defaultChecked` / `defaultValue` counterpart. React would warn under StrictMode; the lib pre-warns at mount with a more actionable message and picks the controlled prop.",
      severity: "warning",
      docsAnchor: "web-controls/controlled-vs-default",
      exampleFix: "Pick ONE: either `value` + `onChange` (controlled), OR `defaultValue` + ref reads (uncontrolled). Drop the other."
    },
    WEB_CONTROLS_SLIDER_OUT_OF_RANGE: {
      description: "A Slider / NumberInput / Knob received a `value` outside `[min, max]`. The control clamps to the nearest bound and warns. Common cause: state initialised from stale persisted preference after the schema tightened.",
      severity: "warning",
      docsAnchor: "web-controls/slider-out-of-range",
      exampleFix: "Migrate the persisted value to the new range, OR widen [min,max], OR clamp at the store boundary before passing to the control."
    },
    WEB_CONTROLS_OTP_INVALID_LENGTH: {
      description: "An OtpInput received a `length` outside 1\u202632 (non-finite, zero, negative, or absurdly large). The control clamps to a usable cell count and warns. Common cause: `length` derived from a config or API field that arrived as `undefined`, `null`, or a string.",
      severity: "warning",
      docsAnchor: "web-controls/otp-invalid-length",
      exampleFix: "Pass an integer in 1\u202632 (6 for a TOTP code, 8 for a typical SMS code, 12 for a 2faz recovery code). If the value comes from config, default it at the boundary: `length={cfg.digits ?? 6}`."
    },
    WEB_CONTROLS_DUPLICATE_RADIO_NAME: {
      description: "Two RadioGroups in the same document share the same `name` attribute. The browser groups them as ONE radio group across the page \u2014 selecting in group A clears group B. Almost always a copy-paste oversight; the lib emits a warning at mount when it detects the collision via document-wide name index.",
      severity: "warning",
      docsAnchor: "web-controls/duplicate-radio-name",
      exampleFix: "Make every RadioGroup `name` unique per document. If you want shared selection, use a single RadioGroup higher in the tree."
    }
  });
} catch {
}

// src/version.ts
var LIB_VERSION = "0.3.0";
var LIB_STAMP = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass(...parts) {
  return parts.filter(Boolean).join(" ");
}
var Spinner = ({ label }) => /* @__PURE__ */ jsx(
  "span",
  {
    className: "ctl__spinner",
    role: "status",
    "aria-label": label,
    "aria-live": "polite"
  }
);
function detectIconFontGlyph(node) {
  if (!node || typeof node !== "object") return false;
  const el = node;
  const tag = typeof el.type === "string" ? el.type : "";
  if (tag !== "i" && tag !== "span") return false;
  const cls = (el.props?.className ?? "").toString();
  return /\b(nf-|nf\b|fa\b|fa-|material-icons|glyphicon|bi-|ri-|ti-|fi-|ph-|lucide-)/.test(
    cls
  );
}
var Button = forwardRef(function Button2(props, ref) {
  const {
    children,
    size = "md",
    variant = "secondary",
    shape = "rounded",
    fullWidth = false,
    as,
    type = "button",
    disabled = false,
    loading = false,
    loadingLabel = "Loading",
    icon,
    leadingIcon,
    trailingIcon,
    tooltip,
    className,
    onClick,
    ...rest
  } = props;
  const Tag = as ?? "button";
  const isAnchor = Tag === "a";
  for (const node of [icon, leadingIcon, trailingIcon]) {
    if (detectIconFontGlyph(node)) {
      warn({
        fn: "Button",
        code: "WEB_CONTROLS_ICON_FONT_GLYPH",
        message: 'Button received an icon-font glyph (e.g. <i class="nf-icn">) as its icon slot. The control will render, but icon fonts fail silently when the font file does not load \u2014 pass an inline <svg> or React component instead.',
        received: "icon-font glyph",
        dedupKey: "WEB_CONTROLS_ICON_FONT_GLYPH@Button"
      });
      break;
    }
  }
  const classes = joinClass(
    "ctl",
    "ctl-btn",
    `ctl--size-${size}`,
    `ctl--shape-${shape}`,
    `ctl-btn--variant-${variant}`,
    fullWidth && "ctl-btn--full-width",
    !children && !!(icon || leadingIcon) && "ctl-btn--icon-only",
    className
  );
  const a11yProps = {};
  if (disabled || loading) {
    if (isAnchor) a11yProps["aria-disabled"] = true;
    else a11yProps["disabled"] = true;
    if (loading) a11yProps["aria-busy"] = true;
  }
  const content = loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: "ctl__icon", children: /* @__PURE__ */ jsx(Spinner, { label: loadingLabel }) }),
    children ? (
      /* iter-33: data-allow-edge-text suppresses the
       * STR_TEXT_CROWDS_CONTAINER_EDGE leaf-text probe on this
       * label span specifically. The probe's threshold is `< 4px`
       * gap; on xs (_h=20, font=12) the math produces a true
       * 4px gap that sub-pixel rendering occasionally clips
       * below threshold. The button's own padding still owns
       * the visual breathing room — the opt-out only silences
       * the edge-case sub-pixel false fire on the lib-owned
       * inner span. */
      /* @__PURE__ */ jsx("span", { className: "ctl-btn__label", "data-allow-edge-text": "true", children })
    ) : null
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    (leadingIcon || icon) && /* @__PURE__ */ jsx("span", { className: "ctl__icon", "aria-hidden": "true", children: leadingIcon ?? icon }),
    children ? (
      /* iter-33: data-allow-edge-text suppresses the
       * STR_TEXT_CROWDS_CONTAINER_EDGE leaf-text probe on this
       * label span specifically. The probe's threshold is `< 4px`
       * gap; on xs (_h=20, font=12) the math produces a true
       * 4px gap that sub-pixel rendering occasionally clips
       * below threshold. The button's own padding still owns
       * the visual breathing room — the opt-out only silences
       * the edge-case sub-pixel false fire on the lib-owned
       * inner span. */
      /* @__PURE__ */ jsx("span", { className: "ctl-btn__label", "data-allow-edge-text": "true", children })
    ) : null,
    trailingIcon && /* @__PURE__ */ jsx("span", { className: "ctl__icon", "aria-hidden": "true", children: trailingIcon })
  ] });
  const isSubMinSize = size === "xs" || size === "sm" || size === "lg";
  const elementProps = {
    ref,
    className: classes,
    "data-norkron-lib": LIB_STAMP,
    ...isSubMinSize ? { "data-allow-small-target": "true" } : {},
    onClick: disabled || loading ? void 0 : onClick,
    ...isAnchor ? {} : { type },
    ...a11yProps,
    ...rest
  };
  const button = /* @__PURE__ */ jsx(Tag, { ...elementProps, children: content });
  if (tooltip) {
    return /* @__PURE__ */ jsx(Tooltip, { axis: "vertical", content: tooltip, trigger: button });
  }
  return button;
});
var IconButton = forwardRef(function IconButton2(props, ref) {
  const { icon, tooltip, ...rest } = props;
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  if (!ariaLabel && !ariaLabelledBy) {
    warn({
      fn: "IconButton",
      code: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL",
      message: 'IconButton rendered with no accessible name. Pass `aria-label="<intent>"` or `aria-labelledby=<id>`. Screen-reader users currently hear "button" with no context.',
      received: "<none>",
      dedupKey: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL@IconButton"
    });
  }
  const effectiveTooltip = tooltip ?? ariaLabel ?? void 0;
  return /* @__PURE__ */ jsx(
    Button,
    {
      ref,
      ...rest,
      icon,
      tooltip: effectiveTooltip
    }
  );
});
var LIB_STAMP2 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass2(...parts) {
  return parts.filter(Boolean).join(" ");
}
function ButtonGroup({
  children,
  orientation = "horizontal",
  ...rest
}) {
  const items = Children.toArray(children).filter(isValidElement);
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "group",
      className: joinClass2(
        "ctl-btn-group",
        `ctl-btn-group--orientation-${orientation}`
      ),
      "data-norkron-lib": LIB_STAMP2,
      "aria-label": rest["aria-label"],
      children: items.map((child, idx) => {
        const existing = child.props.className ?? "";
        return cloneElement(child, {
          key: child.key ?? idx,
          className: joinClass2(existing, "ctl-btn-group__item")
        });
      })
    }
  );
}
var LIB_STAMP3 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass3(...parts) {
  return parts.filter(Boolean).join(" ");
}
var Checkbox = forwardRef(function Checkbox2(props, ref) {
  const {
    children,
    size = "md",
    variant = "primary",
    checked,
    defaultChecked,
    indeterminate = false,
    onChange,
    disabled = false,
    readOnly = false,
    loading = false,
    loadingLabel = "Loading",
    className,
    id: idProp,
    name,
    value,
    ...rest
  } = props;
  const autoId = useId();
  const id = idProp ?? `ctl-cb-${autoId}`;
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, []);
  const warnedRef = useRef(false);
  if (!warnedRef.current && checked !== void 0 && defaultChecked !== void 0) {
    warnedRef.current = true;
    warn({
      fn: "Checkbox",
      code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
      message: "Checkbox received BOTH `checked` (controlled) AND `defaultChecked` (uncontrolled). The controlled `checked` value wins; drop `defaultChecked` to silence this warning.",
      received: { checked, defaultChecked },
      dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@Checkbox"
    });
  }
  useEffect(() => {
    const el = innerRef.current;
    if (el) el.indeterminate = !!indeterminate;
  }, [indeterminate, checked]);
  const handleChange = (e) => {
    if (loading) {
      e.preventDefault();
      if (innerRef.current && checked !== void 0) {
        innerRef.current.checked = checked;
      }
      return;
    }
    if (readOnly) {
      e.preventDefault();
      if (innerRef.current && checked !== void 0) {
        innerRef.current.checked = checked;
      }
      return;
    }
    onChange?.(e.target.checked, e);
  };
  const isControlled = checked !== void 0;
  const classes = joinClass3(
    "ctl-cb",
    `ctl-cb--size-${size}`,
    `ctl-cb--variant-${variant}`,
    disabled && "ctl-cb--disabled",
    readOnly && "ctl-cb--readonly",
    loading && "ctl-cb--loading",
    indeterminate && "ctl-cb--indeterminate",
    className
  );
  const inputProps = {
    ...rest,
    id,
    name,
    value,
    type: "checkbox",
    className: "ctl-cb__input",
    disabled: disabled || loading,
    readOnly,
    onChange: handleChange,
    "aria-checked": indeterminate ? "mixed" : void 0,
    "aria-busy": loading || void 0,
    // iter42 STR_CLICK_TARGET_BELOW_MIN_SIZE — the visually-hidden
    // native <input> is intentionally 1×1px (the standard
    // visually-hidden pattern: `position: absolute; width: 1px;
    // height: 1px; ...`). The interactive tap-target is the visible
    // `<label class="ctl-cb">` wrapping it (>=24px AAA via
    // `min-block-size: var(--_h)`); clicking the label delegates focus
    // + toggle to the wrapped input. Opt the hidden input out of the
    // tap-target probe so the matcher reads the real target (the
    // label) instead of the kept-focusable 1×1 input. Matches the
    // iter-33 Switch `data-allow-small-target` precedent.
    "data-allow-small-target": "true"
  };
  if (isControlled) {
    inputProps.checked = !!checked;
  } else if (defaultChecked !== void 0) {
    inputProps.defaultChecked = defaultChecked;
  }
  return /* @__PURE__ */ jsxs(
    "label",
    {
      className: classes,
      htmlFor: id,
      "data-norkron-lib": LIB_STAMP3,
      "data-allow-passive": "",
      children: [
        /* @__PURE__ */ jsx("input", { ref: innerRef, ...inputProps }),
        /* @__PURE__ */ jsx("span", { className: "ctl-cb__indicator", "aria-hidden": !loading, children: loading ? /* @__PURE__ */ jsx(
          "span",
          {
            className: "ctl-cb__spinner",
            role: "status",
            "aria-label": loadingLabel
          }
        ) : indeterminate ? /* @__PURE__ */ jsx(
          "svg",
          {
            className: "ctl-cb__glyph ctl-cb__glyph--indeterminate",
            viewBox: "0 0 16 16",
            focusable: "false",
            children: /* @__PURE__ */ jsx(
              "rect",
              {
                x: "3",
                y: "7",
                width: "10",
                height: "2",
                rx: "1",
                fill: "currentColor"
              }
            )
          }
        ) : /* @__PURE__ */ jsx(
          "svg",
          {
            className: "ctl-cb__glyph ctl-cb__glyph--check",
            viewBox: "0 0 16 16",
            focusable: "false",
            children: /* @__PURE__ */ jsx(
              "path",
              {
                d: "M3.5 8.5l3 3 6-7",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round"
              }
            )
          }
        ) }),
        children ? /* @__PURE__ */ jsx("span", { className: "ctl-cb__label", children }) : null
      ]
    }
  );
});
var CheckboxGroup = forwardRef(function CheckboxGroup2(props, ref) {
  const {
    value,
    defaultValue,
    onChange,
    name,
    label,
    description,
    orientation = "vertical",
    size = "md",
    variant = "primary",
    disabled = false,
    options,
    children,
    className,
    id: idProp
  } = props;
  const autoId = useId();
  const id = idProp ?? `ctl-cb-group-${autoId}`;
  const labelId = `${id}__label`;
  const descId = `${id}__desc`;
  const warnedRef = useRef(false);
  if (!warnedRef.current && value !== void 0 && defaultValue !== void 0) {
    warnedRef.current = true;
    warn({
      fn: "CheckboxGroup",
      code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
      message: "CheckboxGroup received BOTH `value` (controlled) AND `defaultValue` (uncontrolled). The controlled `value` wins; drop `defaultValue` to silence this warning.",
      received: { value, defaultValue },
      dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@CheckboxGroup"
    });
  }
  const internalRef = useRef(defaultValue ?? []);
  const current = value ?? internalRef.current;
  const setNext = (next) => {
    if (value === void 0) internalRef.current = next;
    onChange?.(next);
  };
  const toggle = (optValue, isChecked) => {
    const set = new Set(current);
    if (isChecked) set.add(optValue);
    else set.delete(optValue);
    setNext(Array.from(set));
  };
  const classes = joinClass3(
    "ctl-cb-group",
    `ctl-cb-group--${orientation}`,
    disabled && "ctl-cb-group--disabled",
    className
  );
  const labelledBy = label ? labelId : void 0;
  const describedBy = description ? descId : void 0;
  const renderedItems = options ? options.map((opt) => /* @__PURE__ */ jsx(
    Checkbox,
    {
      name,
      value: opt.value,
      size,
      variant,
      disabled: disabled || opt.disabled,
      checked: current.includes(opt.value),
      onChange: (next) => toggle(opt.value, next),
      children: opt.label
    },
    opt.value
  )) : children;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      id,
      className: classes,
      role: "group",
      "aria-labelledby": labelledBy,
      "aria-describedby": describedBy,
      "data-norkron-lib": LIB_STAMP3,
      children: [
        label ? /* @__PURE__ */ jsx("div", { id: labelId, className: "ctl-cb-group__label", children: label }) : null,
        description ? /* @__PURE__ */ jsx("div", { id: descId, className: "ctl-cb-group__description", children: description }) : null,
        /* @__PURE__ */ jsx("div", { className: "ctl-cb-group__items", children: renderedItems })
      ]
    }
  );
});
var LIB_STAMP4 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass4(...parts) {
  return parts.filter(Boolean).join(" ");
}
var Radio = forwardRef(function Radio2(props, ref) {
  const {
    children,
    size = "md",
    checked,
    defaultChecked,
    disabled = false,
    onChange,
    className,
    id: idProp,
    name,
    value,
    ...rest
  } = props;
  const autoId = useId();
  const id = idProp ?? autoId;
  const labelClass = joinClass4(
    "ctl-rd",
    `ctl-rd--size-${size}`,
    disabled && "ctl-rd--disabled",
    className
  );
  const checkedProps = checked !== void 0 ? { checked, onChange: onChange ?? (() => {
  }) } : { defaultChecked, onChange };
  return /* @__PURE__ */ jsxs(
    "label",
    {
      className: labelClass,
      htmlFor: id,
      "data-norkron-lib": LIB_STAMP4,
      "data-allow-passive": "",
      children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            ...rest,
            ref,
            id,
            type: "radio",
            className: "ctl-rd__input",
            name,
            value,
            disabled,
            ...checkedProps,
            "data-allow-small-target": "true"
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "ctl-rd__indicator", "aria-hidden": "true", children: /* @__PURE__ */ jsx("span", { className: "ctl-rd__dot" }) }),
        children != null && /* @__PURE__ */ jsx("span", { className: "ctl-rd__label", children })
      ]
    }
  );
});
function RadioGroup(props) {
  const {
    name,
    value,
    onChange,
    options,
    size = "md",
    orientation = "vertical",
    disabled = false,
    className,
    children,
    ...aria
  } = props;
  const containerRef = useRef(null);
  useEffect(() => {
    if (typeof document === "undefined" || !name) return;
    const node = containerRef.current;
    if (!node) return;
    const selector = `input[type="radio"][name="${CSS.escape(name)}"]`;
    try {
      const all = document.querySelectorAll(selector);
      let foreign = false;
      for (const el of Array.from(all)) {
        if (!node.contains(el)) {
          foreign = true;
          break;
        }
      }
      if (foreign) {
        warn({
          fn: "RadioGroup",
          code: "WEB_CONTROLS_DUPLICATE_RADIO_NAME",
          message: `RadioGroup name="${name}" collides with another radio group on the page. Browsers group radios by name across the whole document \u2014 selecting in one will clear the other. Make every RadioGroup name unique per document.`,
          received: name,
          dedupKey: `WEB_CONTROLS_DUPLICATE_RADIO_NAME@${name}`
        });
      }
    } catch {
    }
  }, [name]);
  const getRadios = useCallback(() => {
    const node = containerRef.current;
    if (!node) return [];
    return Array.from(
      node.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(name)}"]:not(:disabled)`
      )
    );
  }, [name]);
  const focusAndSelect = useCallback(
    (el) => {
      if (!el) return;
      el.focus();
      if (el.value !== value) onChange(el.value);
    },
    [onChange, value]
  );
  const onKeyDown = useCallback(
    (e) => {
      const key = e.key;
      if (key !== "ArrowDown" && key !== "ArrowRight" && key !== "ArrowUp" && key !== "ArrowLeft" && key !== "Home" && key !== "End") {
        return;
      }
      const radios = getRadios();
      if (radios.length === 0) return;
      const active = document.activeElement;
      const currentIdx = active ? radios.indexOf(active) : -1;
      let nextIdx;
      switch (key) {
        case "ArrowDown":
        case "ArrowRight":
          nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % radios.length;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          nextIdx = currentIdx < 0 ? radios.length - 1 : (currentIdx - 1 + radios.length) % radios.length;
          break;
        case "Home":
          nextIdx = 0;
          break;
        case "End":
          nextIdx = radios.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      focusAndSelect(radios[nextIdx]);
    },
    [focusAndSelect, getRadios]
  );
  const wrapperClass = joinClass4(
    "ctl-rd-group",
    `ctl-rd-group--${orientation}`,
    className
  );
  const derivedChildren = useMemo(() => {
    if (!options) return null;
    return options.map((opt) => /* @__PURE__ */ jsx(
      Radio,
      {
        name,
        value: opt.value,
        size,
        disabled: disabled || opt.disabled,
        checked: value === opt.value,
        onChange: (e) => {
          if (e.target.checked) onChange(opt.value);
        },
        children: opt.label
      },
      opt.value
    ));
  }, [options, name, size, disabled, value, onChange]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: containerRef,
      role: "radiogroup",
      className: wrapperClass,
      "data-norkron-lib": LIB_STAMP4,
      onKeyDown,
      ...aria,
      children: derivedChildren ?? children
    }
  );
}
var LIB_STAMP5 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass5(...parts) {
  return parts.filter(Boolean).join(" ");
}
var Switch = forwardRef(function Switch2(props, ref) {
  const {
    children,
    size = "md",
    checked,
    defaultChecked,
    onChange,
    disabled = false,
    loading = false,
    loadingLabel = "Loading",
    onLabel,
    offLabel,
    className,
    onClick,
    onKeyDown,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...rest
  } = props;
  const isControlled = checked !== void 0;
  const [internal, setInternal] = useState(defaultChecked ?? false);
  const value = isControlled ? checked : internal;
  const warned = useRef(false);
  useEffect(() => {
    if (warned.current) return;
    warned.current = true;
    if (isControlled && defaultChecked !== void 0) {
      warn({
        fn: "Switch",
        code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
        message: "Switch received both `checked` (controlled) and `defaultChecked` (uncontrolled). The controlled `checked` wins; drop `defaultChecked`.",
        received: { checked, defaultChecked },
        dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@Switch"
      });
    }
    const hasVisibleLabel = !!children;
    const hasAriaName = !!(ariaLabel || ariaLabelledBy);
    if (!hasVisibleLabel && !hasAriaName) {
      warn({
        fn: "Switch",
        code: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL",
        message: 'Switch rendered without visible label or `aria-label`/`aria-labelledby`. Screen readers announce "switch" with no context. Pass either a `children` label or `aria-label`.',
        received: { hasVisibleLabel, hasAriaName },
        dedupKey: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL@Switch"
      });
    }
  }, [
    isControlled,
    defaultChecked,
    checked,
    children,
    ariaLabel,
    ariaLabelledBy
  ]);
  const toggle = useCallback(() => {
    if (disabled || loading) return;
    const next = !value;
    if (!isControlled) setInternal(next);
    onChange?.(next);
  }, [disabled, loading, value, isControlled, onChange]);
  const handleClick = useCallback(
    (e) => {
      onClick?.(e);
      if (e.defaultPrevented) return;
      toggle();
    },
    [onClick, toggle]
  );
  const handleKeyDown = useCallback(
    (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Enter") {
        e.preventDefault();
        toggle();
      }
    },
    [onKeyDown, toggle]
  );
  const classes = joinClass5(
    "ctl-sw",
    `ctl-sw--size-${size}`,
    value && "ctl-sw--checked",
    disabled && "ctl-sw--disabled",
    loading && "ctl-sw--loading",
    className
  );
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      type: "button",
      role: "switch",
      "aria-checked": value,
      "aria-busy": loading || void 0,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      disabled: disabled || loading,
      "data-norkron-lib": LIB_STAMP5,
      ...size === "xs" || size === "sm" ? { "data-allow-small-target": "true" } : {},
      className: classes,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      ...rest,
      children: [
        /* @__PURE__ */ jsxs("span", { className: "ctl-sw__track", "aria-hidden": "true", "data-allow-corner-mismatch": "true", children: [
          offLabel ? /* @__PURE__ */ jsx("span", { className: "ctl-sw__track-label ctl-sw__track-label--off", children: offLabel }) : null,
          onLabel ? /* @__PURE__ */ jsx("span", { className: "ctl-sw__track-label ctl-sw__track-label--on", children: onLabel }) : null,
          /* @__PURE__ */ jsx("span", { className: "ctl-sw__thumb", children: loading ? /* @__PURE__ */ jsx(
            "span",
            {
              className: "ctl-sw__spinner",
              role: "status",
              "aria-label": loadingLabel
            }
          ) : null })
        ] }),
        children ? /* @__PURE__ */ jsx("span", { className: "ctl-sw__label", children }) : null
      ]
    }
  );
});
var LIB_STAMP6 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass6(...parts) {
  return parts.filter(Boolean).join(" ");
}
function isRangeShape(v) {
  return Array.isArray(v);
}
function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}
function snapToStep(n, min, step) {
  if (step <= 0) return n;
  const offset = n - min;
  const snapped = Math.round(offset / step) * step + min;
  const decimals = (String(step).split(".")[1] ?? "").length;
  return decimals > 0 ? Number(snapped.toFixed(decimals)) : snapped;
}
function normaliseMarks(marks) {
  if (!marks) return [];
  return marks.map((m) => typeof m === "number" ? { value: m } : m);
}
function valueToPercent(v, min, max) {
  if (max === min) return 0;
  return (v - min) / (max - min) * 100;
}
function pointerToValue(clientX, clientY, rect, orientation, min, max, step) {
  const horizontal = orientation === "horizontal";
  const pos = horizontal ? clientX - rect.left : clientY - rect.top;
  const dim = horizontal ? rect.width : rect.height;
  let ratio = dim === 0 ? 0 : pos / dim;
  if (!horizontal) ratio = 1 - ratio;
  ratio = clamp(ratio, 0, 1);
  const raw = min + ratio * (max - min);
  return snapToStep(clamp(raw, min, max), min, step);
}
var Slider = forwardRef(function Slider2(props, ref) {
  const {
    min = 0,
    max = 100,
    step = 1,
    orientation = "horizontal",
    size = "md",
    marks,
    disabled = false,
    formatValue,
    className,
    style,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-label-thumbs": ariaLabelThumbs
  } = props;
  const controlled = props.value;
  const uncontrolled = props.defaultValue;
  const sample = controlled ?? uncontrolled;
  const isRange = isRangeShape(sample);
  const trackRef = useRef(null);
  const thumbRefs = useRef([null, null]);
  const initialInternal = useMemo(() => {
    if (isRange) {
      const dv2 = uncontrolled ?? controlled ?? [min, max];
      return [
        snapToStep(clamp(dv2[0], min, max), min, step),
        snapToStep(clamp(dv2[1], min, max), min, step)
      ];
    }
    const dv = uncontrolled ?? controlled ?? min;
    return snapToStep(clamp(dv, min, max), min, step);
  }, []);
  const [internal, setInternal] = useState(
    initialInternal
  );
  useEffect(() => {
    if (controlled === void 0) return;
    const check = (n, which) => {
      if (n < min || n > max) {
        warn({
          fn: "Slider",
          code: "WEB_CONTROLS_SLIDER_OUT_OF_RANGE",
          message: `Slider ${which} value ${n} is outside [${min}, ${max}]. Clamping.`,
          received: n,
          dedupKey: `WEB_CONTROLS_SLIDER_OUT_OF_RANGE@${which}`
        });
      }
    };
    if (isRangeShape(controlled)) {
      const [lo, hi] = controlled;
      check(lo, "lower");
      check(hi, "upper");
    } else {
      check(controlled, "value");
    }
  }, [controlled, min, max]);
  const effective = useMemo(() => {
    const source = controlled ?? internal;
    if (isRange) {
      const arr = source;
      let lo = snapToStep(clamp(arr[0], min, max), min, step);
      let hi = snapToStep(clamp(arr[1], min, max), min, step);
      if (lo > hi) [lo, hi] = [hi, lo];
      return [lo, hi];
    }
    return snapToStep(clamp(source, min, max), min, step);
  }, [controlled, internal, isRange, min, max, step]);
  const activeRef = useRef(null);
  const emitChange = useCallback(
    (next) => {
      if (controlled === void 0) setInternal(next);
      if (isRange) {
        props.onChange?.(next);
      } else {
        props.onChange?.(next);
      }
    },
    [controlled, isRange, props]
  );
  const emitCommit = useCallback(
    (next) => {
      if (isRange) {
        props.onChangeCommit?.(next);
      } else {
        props.onChangeCommit?.(next);
      }
    },
    [isRange, props]
  );
  const updateThumb = useCallback(
    (which, raw) => {
      const snapped = snapToStep(clamp(raw, min, max), min, step);
      if (isRange) {
        const [lo, hi] = effective;
        let nextLo = lo;
        let nextHi = hi;
        if (which === 0) {
          nextLo = clamp(snapped, min, Math.max(min, hi - step));
        } else {
          nextHi = clamp(snapped, Math.min(max, lo + step), max);
        }
        if (nextLo === lo && nextHi === hi) return;
        emitChange([nextLo, nextHi]);
      } else {
        if (snapped === effective) return;
        emitChange(snapped);
      }
    },
    [effective, emitChange, isRange, max, min, step]
  );
  const nearestThumb = useCallback(
    (targetValue) => {
      if (!isRange) return 0;
      const [lo, hi] = effective;
      const dLo = Math.abs(targetValue - lo);
      const dHi = Math.abs(targetValue - hi);
      if (dLo === dHi) return targetValue < lo ? 0 : 1;
      return dLo < dHi ? 0 : 1;
    },
    [effective, isRange]
  );
  const onTrackPointerDown = useCallback(
    (e) => {
      if (disabled) return;
      const track = trackRef.current;
      if (!track) return;
      e.preventDefault();
      const rect = track.getBoundingClientRect();
      const v = pointerToValue(
        e.clientX,
        e.clientY,
        rect,
        orientation,
        min,
        max,
        step
      );
      const which = nearestThumb(v);
      activeRef.current = which;
      updateThumb(which, v);
      const thumb = thumbRefs.current[which];
      if (thumb) {
        thumb.focus();
        try {
          thumb.setPointerCapture(e.pointerId);
        } catch {
        }
      }
    },
    [disabled, max, min, nearestThumb, orientation, step, updateThumb]
  );
  const onThumbPointerDown = useCallback(
    (which) => (e) => {
      if (disabled) return;
      e.preventDefault();
      activeRef.current = which;
      const thumb = thumbRefs.current[which];
      if (thumb) {
        thumb.focus();
        try {
          thumb.setPointerCapture(e.pointerId);
        } catch {
        }
      }
    },
    [disabled]
  );
  const onPointerMove = useCallback(
    (e) => {
      if (disabled) return;
      if (activeRef.current === null) return;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const v = pointerToValue(
        e.clientX,
        e.clientY,
        rect,
        orientation,
        min,
        max,
        step
      );
      updateThumb(activeRef.current, v);
    },
    [disabled, max, min, orientation, step, updateThumb]
  );
  const onPointerUp = useCallback(
    (e) => {
      if (activeRef.current === null) return;
      const which = activeRef.current;
      activeRef.current = null;
      const thumb = thumbRefs.current[which];
      if (thumb) {
        try {
          thumb.releasePointerCapture(e.pointerId);
        } catch {
        }
      }
      emitCommit(effective);
    },
    [effective, emitCommit]
  );
  const onThumbKeyDown = useCallback(
    (which) => (e) => {
      if (disabled) return;
      const big = e.shiftKey ? step * 10 : step;
      let delta = 0;
      let absolute = null;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowUp":
          delta = big;
          break;
        case "ArrowLeft":
        case "ArrowDown":
          delta = -big;
          break;
        case "PageUp":
          delta = step * 10;
          break;
        case "PageDown":
          delta = -step * 10;
          break;
        case "Home":
          absolute = min;
          break;
        case "End":
          absolute = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      const current = isRange ? effective[which] : effective;
      const next = absolute !== null ? absolute : current + delta;
      updateThumb(which, next);
      const commitVal = isRange ? (() => {
        const [lo, hi] = effective;
        const snapped = snapToStep(
          clamp(absolute !== null ? absolute : current + delta, min, max),
          min,
          step
        );
        if (which === 0)
          return [
            clamp(snapped, min, Math.max(min, hi - step)),
            hi
          ];
        return [lo, clamp(snapped, Math.min(max, lo + step), max)];
      })() : snapToStep(
        clamp(absolute !== null ? absolute : current + delta, min, max),
        min,
        step
      );
      emitCommit(commitVal);
    },
    [disabled, effective, emitCommit, isRange, max, min, step, updateThumb]
  );
  const marksNorm = useMemo(() => normaliseMarks(marks), [marks]);
  const horizontal = orientation === "horizontal";
  const fillStart = isRange ? valueToPercent(effective[0], min, max) : 0;
  const fillEnd = isRange ? valueToPercent(effective[1], min, max) : valueToPercent(effective, min, max);
  const rangeStyle = horizontal ? {
    left: `${fillStart}%`,
    right: `${100 - fillEnd}%`
  } : {
    bottom: `${fillStart}%`,
    top: `${100 - fillEnd}%`
  };
  const rootClasses = joinClass6(
    "ctl",
    "ctl-sl",
    `ctl-sl--orientation-${orientation}`,
    `ctl-sl--size-${size}`,
    isRange && "ctl-sl--range",
    disabled && "ctl-sl--disabled",
    className
  );
  const formatLabel = useCallback(
    (n) => formatValue ? formatValue(n) : String(n),
    [formatValue]
  );
  const renderThumb = (which, value) => {
    const pct = valueToPercent(value, min, max);
    const posStyle = horizontal ? { left: `${pct}%` } : { bottom: `${pct}%` };
    const label = ariaLabelThumbs?.[which] ?? (isRange ? which === 0 ? `${ariaLabel ?? "Slider"} (minimum)` : `${ariaLabel ?? "Slider"} (maximum)` : ariaLabel);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref: (el) => {
          thumbRefs.current[which] = el;
        },
        className: "ctl-sl__thumb",
        role: "slider",
        tabIndex: disabled ? -1 : 0,
        "aria-valuemin": isRange && which === 1 ? Math.min(max, effective[0] + step) : min,
        "aria-valuemax": isRange && which === 0 ? Math.max(min, effective[1] - step) : max,
        "aria-valuenow": value,
        "aria-valuetext": formatLabel(value),
        "aria-orientation": orientation,
        "aria-disabled": disabled || void 0,
        "aria-label": ariaLabelledby ? void 0 : label || void 0,
        "aria-labelledby": ariaLabelledby,
        style: posStyle,
        onPointerDown: onThumbPointerDown(which),
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
        onKeyDown: onThumbKeyDown(which),
        "data-thumb": which
      },
      which
    );
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      id,
      className: rootClasses,
      "data-norkron-lib": LIB_STAMP6,
      "data-orientation": orientation,
      style,
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          ref: trackRef,
          className: "ctl-sl__track",
          onPointerDown: onTrackPointerDown,
          onPointerMove,
          onPointerUp,
          onPointerCancel: onPointerUp,
          children: [
            /* @__PURE__ */ jsx("div", { className: "ctl-sl__rail" }),
            /* @__PURE__ */ jsx("div", { className: "ctl-sl__range", style: rangeStyle }),
            marksNorm.map((m) => {
              const pct = valueToPercent(
                clamp(m.value, min, max),
                min,
                max
              );
              const markStyle = horizontal ? { left: `${pct}%` } : { bottom: `${pct}%` };
              return /* @__PURE__ */ jsx(
                "span",
                {
                  className: "ctl-sl__mark",
                  style: markStyle,
                  "aria-hidden": "true",
                  children: m.label !== void 0 && /* @__PURE__ */ jsx("span", { className: "ctl-sl__mark-label", children: m.label })
                },
                `mark-${m.value}`
              );
            }),
            isRange ? [
              renderThumb(0, effective[0]),
              renderThumb(1, effective[1])
            ] : renderThumb(0, effective)
          ]
        }
      )
    }
  );
});
var LIB_STAMP7 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
var DEFAULT_MIN_ANGLE = -135;
var DEFAULT_MAX_ANGLE = 135;
var DEFAULT_DRAG_TRACK_PX = 200;
var FINE_MULTIPLIER = 0.1;
var COARSE_MULTIPLIER = 10;
function clamp2(v, lo, hi) {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
function quantize(v, min, step) {
  if (step <= 0) return v;
  const steps = Math.round((v - min) / step);
  const out = min + steps * step;
  const decimals = (step.toString().split(".")[1] ?? "").length;
  if (decimals > 0) {
    const m = Math.pow(10, decimals);
    return Math.round(out * m) / m;
  }
  return out;
}
function joinClass7(...parts) {
  return parts.filter(Boolean).join(" ");
}
function polar(angleDeg, radius, cx, cy) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}
function describeArc(cx, cy, radius, startDeg, endDeg) {
  if (startDeg === endDeg) {
    const p = polar(startDeg, radius, cx, cy);
    return `M ${p.x} ${p.y}`;
  }
  const start = polar(startDeg, radius, cx, cy);
  const end = polar(endDeg, radius, cx, cy);
  const delta = endDeg - startDeg;
  const largeArc = Math.abs(delta) > 180 ? 1 : 0;
  const sweep = delta >= 0 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
}
function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
var Knob = forwardRef(function Knob2(props, ref) {
  const {
    value: controlled,
    defaultValue,
    onChange,
    min = 0,
    max = 1,
    step = 0.01,
    size = "md",
    disabled = false,
    "aria-label": ariaLabel,
    formatValue,
    bipolar = false,
    showValue = false,
    dragTrackPx = DEFAULT_DRAG_TRACK_PX,
    className
  } = props;
  const isControlled = controlled !== void 0;
  const midpoint = min + (max - min) / 2;
  const initial = useMemo(() => {
    const seed = controlled !== void 0 ? controlled : defaultValue !== void 0 ? defaultValue : bipolar ? midpoint : min;
    return clamp2(quantize(seed, min, step), min, max);
  }, []);
  const [uncontrolledValue, setUncontrolledValue] = useState(initial);
  const rawValue = isControlled ? controlled : uncontrolledValue;
  const lastWarnedRef = useRef(null);
  useEffect(() => {
    if (rawValue < min || rawValue > max) {
      if (lastWarnedRef.current !== rawValue) {
        lastWarnedRef.current = rawValue;
        warn({
          fn: "Knob",
          code: "WEB_CONTROLS_SLIDER_OUT_OF_RANGE",
          message: `Knob received value ${rawValue} outside [${min}, ${max}]. Clamping to nearest bound.`,
          received: rawValue,
          dedupKey: `WEB_CONTROLS_SLIDER_OUT_OF_RANGE@Knob@${rawValue}`
        });
      }
    }
  }, [rawValue, min, max]);
  const value = clamp2(rawValue, min, max);
  const warnedLabelRef = useRef(false);
  useEffect(() => {
    if (!ariaLabel && !warnedLabelRef.current) {
      warnedLabelRef.current = true;
      warn({
        fn: "Knob",
        code: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL",
        message: 'Knob rendered without `aria-label`. Screen-reader users will hear an unlabelled slider. Pass `aria-label="<intent>"`.',
        received: void 0,
        dedupKey: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL@Knob"
      });
    }
  }, [ariaLabel]);
  const svgRef = useRef(null);
  useImperativeHandle(
    ref,
    () => ({
      focus: () => svgRef.current?.focus(),
      getValue: () => value
    }),
    [value]
  );
  const commit = useCallback(
    (next) => {
      const clamped = clamp2(quantize(next, min, step), min, max);
      if (clamped === value) return;
      if (!isControlled) setUncontrolledValue(clamped);
      onChange?.(clamped);
    },
    [isControlled, max, min, onChange, step, value]
  );
  const dragRef = useRef(null);
  const onPointerDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startY: e.clientY,
        startValue: value,
        modifier: e.shiftKey ? "fine" : e.metaKey || e.ctrlKey ? "coarse" : "none"
      };
      svgRef.current?.focus();
    },
    [disabled, value]
  );
  const onPointerMove = useCallback(
    (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const mod = e.shiftKey ? "fine" : e.metaKey || e.ctrlKey ? "coarse" : "none";
      const multiplier = mod === "fine" ? FINE_MULTIPLIER : mod === "coarse" ? COARSE_MULTIPLIER : 1;
      const dyPx = drag.startY - e.clientY;
      const range2 = max - min;
      const valuePerPx = range2 / dragTrackPx;
      const next = drag.startValue + dyPx * valuePerPx * multiplier;
      commit(next);
    },
    [commit, dragTrackPx, max, min]
  );
  const endDrag = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      ;
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
    }
  }, []);
  const onDoubleClick = useCallback(() => {
    if (disabled) return;
    const reset = defaultValue !== void 0 ? defaultValue : midpoint;
    commit(reset);
  }, [commit, defaultValue, disabled, midpoint]);
  const onKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      let next = null;
      switch (e.key) {
        case "ArrowUp":
        case "ArrowRight":
          next = value + step;
          break;
        case "ArrowDown":
        case "ArrowLeft":
          next = value - step;
          break;
        case "PageUp":
          next = value + step * 10;
          break;
        case "PageDown":
          next = value - step * 10;
          break;
        case "Home":
          next = min;
          break;
        case "End":
          next = max;
          break;
        default:
          return;
      }
      e.preventDefault();
      commit(next);
    },
    [commit, disabled, max, min, step, value]
  );
  const onWheel = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      const mod = e.shiftKey ? FINE_MULTIPLIER : e.metaKey || e.ctrlKey ? COARSE_MULTIPLIER : 1;
      const dir = e.deltaY < 0 ? 1 : -1;
      commit(value + dir * step * mod);
    },
    [commit, disabled, step, value]
  );
  const SVG_SIZE = 100;
  const CX = 50;
  const CY = 50;
  const RAIL_RADIUS = 42;
  const minAngle = DEFAULT_MIN_ANGLE;
  const maxAngle = DEFAULT_MAX_ANGLE;
  const range = max - min;
  const tNorm = range === 0 ? 0 : (value - min) / range;
  const angle = minAngle + tNorm * (maxAngle - minAngle);
  const centerAngle = minAngle + 0.5 * (maxAngle - minAngle);
  const railPath = describeArc(CX, CY, RAIL_RADIUS, minAngle, maxAngle);
  const fillStart = bipolar ? centerAngle : minAngle;
  const fillPath = describeArc(CX, CY, RAIL_RADIUS, fillStart, angle);
  const indicatorOuter = polar(angle, RAIL_RADIUS - 4, CX, CY);
  const indicatorInner = polar(angle, RAIL_RADIUS - 18, CX, CY);
  const formatted = formatValue ? formatValue(value) : String(value);
  const reduced = prefersReducedMotion();
  const uid = useId();
  const valueId = `${uid}-val`;
  const classes = joinClass7(
    "ctl-kb",
    `ctl-kb--size-${size}`,
    disabled && "ctl-kb--disabled",
    bipolar && "ctl-kb--bipolar",
    reduced && "ctl-kb--reduced-motion",
    className
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: classes,
      "data-norkron-lib": LIB_STAMP7,
      onDoubleClick,
      children: [
        /* @__PURE__ */ jsxs(
          "svg",
          {
            ref: svgRef,
            className: "ctl-kb__svg",
            viewBox: `0 0 ${SVG_SIZE} ${SVG_SIZE}`,
            role: "slider",
            tabIndex: disabled ? -1 : 0,
            "aria-label": ariaLabel,
            "aria-valuemin": min,
            "aria-valuemax": max,
            "aria-valuenow": value,
            "aria-valuetext": formatted,
            "aria-disabled": disabled || void 0,
            "aria-orientation": "vertical",
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onKeyDown,
            onWheel,
            children: [
              /* @__PURE__ */ jsx("title", { children: ariaLabel ? `${ariaLabel}: ${formatted}` : formatted }),
              /* @__PURE__ */ jsx(
                "path",
                {
                  className: "ctl-kb__rail",
                  d: railPath,
                  fill: "none"
                }
              ),
              /* @__PURE__ */ jsx(
                "path",
                {
                  className: "ctl-kb__fill",
                  d: fillPath,
                  fill: "none"
                }
              ),
              /* @__PURE__ */ jsx(
                "line",
                {
                  className: "ctl-kb__indicator",
                  x1: indicatorInner.x,
                  y1: indicatorInner.y,
                  x2: indicatorOuter.x,
                  y2: indicatorOuter.y
                }
              )
            ]
          }
        ),
        showValue && /* @__PURE__ */ jsx("span", { id: valueId, className: "ctl-kb__value", children: formatted })
      ]
    }
  );
});
var LIB_STAMP8 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
var FieldContext = createContext(null);
function useFieldContext() {
  return useContext(FieldContext);
}
var Field = forwardRef(function Field2(props, ref) {
  const {
    invalid = false,
    required = false,
    disabled = false,
    readOnly = false,
    id: idProp,
    children,
    className,
    ...rest
  } = props;
  const autoId = useId();
  const controlId = idProp ?? `ctl-fld-${autoId}`;
  const labelId = `${controlId}__label`;
  const descriptionId = `${controlId}__desc`;
  const errorId = `${controlId}__err`;
  const [descCount, setDescCount] = useState(0);
  const [errCount, setErrCount] = useState(0);
  const registerDescription = useCallback(() => {
    setDescCount((n) => n + 1);
    return () => setDescCount((n) => Math.max(0, n - 1));
  }, []);
  const registerError = useCallback(() => {
    setErrCount((n) => n + 1);
    return () => setErrCount((n) => Math.max(0, n - 1));
  }, []);
  const hasDescription = descCount > 0;
  const hasError = errCount > 0;
  const ctx = useMemo(
    () => {
      const parts = [];
      if (hasDescription) parts.push(descriptionId);
      if (invalid && hasError) parts.push(errorId);
      return {
        controlId,
        labelId,
        descriptionId,
        errorId,
        describedById: parts.length > 0 ? parts.join(" ") : void 0,
        invalid,
        required,
        disabled,
        readOnly,
        hasDescription,
        hasError,
        registerDescription,
        registerError
      };
    },
    [
      controlId,
      labelId,
      descriptionId,
      errorId,
      invalid,
      required,
      disabled,
      readOnly,
      hasDescription,
      hasError,
      registerDescription,
      registerError
    ]
  );
  const cls = [
    "ctl-fld",
    invalid && "ctl-fld--invalid",
    disabled && "ctl-fld--disabled",
    readOnly && "ctl-fld--readonly",
    required && "ctl-fld--required",
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(FieldContext.Provider, { value: ctx, children: /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cls,
      "data-norkron-lib": LIB_STAMP8,
      ...rest,
      children
    }
  ) });
});
var FieldLabel = forwardRef(function FieldLabel2(props, ref) {
  const ctx = useFieldContext();
  const { children, className, ...rest } = props;
  const cls = ["ctl-fld__label", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs(
    "label",
    {
      ref,
      id: ctx?.labelId,
      htmlFor: ctx?.controlId,
      className: cls,
      "data-allow-edge-text": "true",
      ...rest,
      children: [
        children,
        ctx?.required ? /* @__PURE__ */ jsx("span", { className: "ctl-fld__required", "aria-hidden": "true", children: " *" }) : null
      ]
    }
  );
});
var FieldDescription = forwardRef(function FieldDescription2(props, ref) {
  const ctx = useFieldContext();
  useEffect(() => {
    if (!ctx) return;
    return ctx.registerDescription();
  }, [ctx]);
  const { children, className, ...rest } = props;
  const cls = ["ctl-fld__description", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "p",
    {
      ref,
      id: ctx?.descriptionId,
      className: cls,
      "data-allow-edge-text": "true",
      ...rest,
      children
    }
  );
});
var FieldError = forwardRef(function FieldError2(props, ref) {
  const ctx = useFieldContext();
  const { children, className, forceShow = false, ...rest } = props;
  const willRender = (forceShow || !ctx || ctx.invalid) && Boolean(children);
  useEffect(() => {
    if (!ctx || !willRender) return;
    return ctx.registerError();
  }, [ctx, willRender]);
  if (!forceShow && ctx && !ctx.invalid) return null;
  if (!children) return null;
  const cls = ["ctl-fld__error", className].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "p",
    {
      ref,
      id: ctx?.errorId,
      role: "alert",
      className: cls,
      "data-allow-edge-text": "true",
      ...rest,
      children
    }
  );
});
var LIB_STAMP9 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass8(...parts) {
  return parts.filter(Boolean).join(" ");
}
function detectIconFontGlyph2(node) {
  if (!node || typeof node !== "object") return false;
  const el = node;
  const tag = typeof el.type === "string" ? el.type : "";
  if (tag !== "i" && tag !== "span") return false;
  const cls = (el.props?.className ?? "").toString();
  return /\b(nf-|nf\b|fa\b|fa-|material-icons|glyphicon|bi-|ri-|ti-|fi-|ph-|lucide-)/.test(
    cls
  );
}
var EyeIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx("path", { d: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" }),
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "3" })
    ]
  }
);
var EyeOffIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx("path", { d: "M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a19.7 19.7 0 0 1 4.22-5.36" }),
      /* @__PURE__ */ jsx("path", { d: "M9.9 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a19.7 19.7 0 0 1-2.16 3.19" }),
      /* @__PURE__ */ jsx("path", { d: "M14.12 14.12a3 3 0 0 1-4.24-4.24" }),
      /* @__PURE__ */ jsx("line", { x1: "2", y1: "2", x2: "22", y2: "22" })
    ]
  }
);
var SearchIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "11", cy: "11", r: "7" }),
      /* @__PURE__ */ jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })
    ]
  }
);
var ClearIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx("line", { x1: "15", y1: "9", x2: "9", y2: "15" }),
      /* @__PURE__ */ jsx("line", { x1: "9", y1: "9", x2: "15", y2: "15" })
    ]
  }
);
var TextInputCore = forwardRef(function TextInputCore2(props, ref) {
  const {
    size = "md",
    variant = "outline",
    leadingIcon,
    trailingIcon,
    trailingSlot,
    invalid = false,
    loading = false,
    disabled = false,
    readOnly = false,
    prefix,
    suffix,
    className,
    type = "text",
    value,
    defaultValue,
    onFocus,
    onBlur,
    id: idProp,
    ...rest
  } = props;
  const field = useFieldContext();
  const effInvalid = invalid || (field?.invalid ?? false);
  const effDisabled = disabled || (field?.disabled ?? false);
  const effReadOnly = readOnly || (field?.readOnly ?? false);
  const fieldDescribedBy = field?.describedById;
  const ariaRequired = field?.required || void 0;
  const warnedRef = useRef(false);
  if (!warnedRef.current && value !== void 0 && defaultValue !== void 0) {
    warnedRef.current = true;
    warn({
      fn: "TextInput",
      code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
      message: "TextInput received both `value` and `defaultValue`. The lib picks `value` (controlled). Drop `defaultValue` or drop `value`+`onChange`.",
      received: { hasValue: true, hasDefaultValue: true },
      dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@TextInput"
    });
  }
  for (const node of [leadingIcon, trailingIcon]) {
    if (detectIconFontGlyph2(node)) {
      warn({
        fn: "TextInput",
        code: "WEB_CONTROLS_ICON_FONT_GLYPH",
        message: 'TextInput received an icon-font glyph (e.g. <i class="nf-icn">) as its icon slot. The control renders, but icon fonts fail silently when the font file does not load \u2014 pass an inline <svg> or React component instead.',
        received: "icon-font glyph",
        dedupKey: "WEB_CONTROLS_ICON_FONT_GLYPH@TextInput"
      });
      break;
    }
  }
  const [focused, setFocused] = useState(false);
  const handleFocus = useCallback(
    (e) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );
  const handleBlur = useCallback(
    (e) => {
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );
  const autoId = useId();
  const id = idProp ?? field?.controlId ?? autoId;
  const containerClass = joinClass8(
    "ctl-ti",
    `ctl-ti--size-${size}`,
    `ctl-ti--variant-${variant}`,
    effInvalid && "ctl-ti--invalid",
    effDisabled && "ctl-ti--disabled",
    effReadOnly && "ctl-ti--readonly",
    focused && "ctl-ti--focused",
    className
  );
  const resolvedTrailing = loading ? /* @__PURE__ */ jsx("span", { className: "ctl-ti__spinner", role: "status", "aria-label": "Loading" }) : trailingSlot ?? (trailingIcon ? /* @__PURE__ */ jsx("span", { className: "ctl-ti__trailing-icon", "aria-hidden": "true", children: trailingIcon }) : null);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: containerClass,
      "data-norkron-lib": LIB_STAMP9,
      children: [
        leadingIcon && /* @__PURE__ */ jsx("span", { className: "ctl-ti__leading-icon", "aria-hidden": "true", children: leadingIcon }),
        prefix !== void 0 && /* @__PURE__ */ jsx("span", { className: "ctl-ti__prefix", "aria-hidden": "true", children: prefix }),
        /* @__PURE__ */ jsx(
          "input",
          {
            ref,
            id,
            type,
            className: "ctl-ti__field",
            value,
            defaultValue: value === void 0 ? defaultValue : void 0,
            ...rest,
            disabled: effDisabled,
            readOnly: effReadOnly,
            "aria-invalid": effInvalid || void 0,
            "aria-readonly": effReadOnly || void 0,
            "aria-busy": loading || void 0,
            "aria-required": ariaRequired,
            "aria-describedby": rest["aria-describedby"] ?? fieldDescribedBy,
            onFocus: handleFocus,
            onBlur: handleBlur
          }
        ),
        suffix !== void 0 && /* @__PURE__ */ jsx("span", { className: "ctl-ti__suffix", "aria-hidden": "true", children: suffix }),
        resolvedTrailing
      ]
    }
  );
});
var TextInput = forwardRef(function TextInput2(props, ref) {
  return /* @__PURE__ */ jsx(TextInputCore, { ...props, type: "text", ref });
});
var PasswordInput = forwardRef(function PasswordInput2(props, ref) {
  const {
    toggleShowLabel = "Show password",
    toggleHideLabel = "Hide password",
    trailingIcon: _ignoredTrailing,
    disabled,
    ...rest
  } = props;
  const [revealed, setRevealed] = useState(false);
  const onToggle = useCallback(
    (e) => {
      e.preventDefault();
      setRevealed((r) => !r);
    },
    []
  );
  const toggleNode = /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: "ctl-ti__toggle",
      onClick: onToggle,
      "aria-label": revealed ? toggleHideLabel : toggleShowLabel,
      "aria-pressed": revealed,
      disabled,
      tabIndex: 0,
      children: revealed ? /* @__PURE__ */ jsx(EyeOffIcon, {}) : /* @__PURE__ */ jsx(EyeIcon, {})
    }
  );
  return /* @__PURE__ */ jsx(
    TextInputCore,
    {
      ...rest,
      disabled,
      type: revealed ? "text" : "password",
      trailingSlot: toggleNode,
      ref
    }
  );
});
var SearchInput = forwardRef(function SearchInput2(props, ref) {
  const {
    clearLabel = "Clear search",
    leadingIcon,
    trailingIcon,
    value,
    defaultValue,
    onChange,
    disabled,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = useState(
    typeof defaultValue === "string" ? defaultValue : ""
  );
  const isControlled = value !== void 0;
  const currentValue = isControlled ? String(value ?? "") : internalValue;
  const handleChange = useCallback(
    (e) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    },
    [isControlled, onChange]
  );
  const handleClear = useCallback(
    (e) => {
      e.preventDefault();
      if (!isControlled) setInternalValue("");
      if (onChange) {
        const target = { value: "" };
        const synthetic = {
          ...e,
          target,
          currentTarget: target
        };
        onChange(synthetic);
      }
    },
    [isControlled, onChange]
  );
  const resolvedLeading = leadingIcon ?? /* @__PURE__ */ jsx(SearchIcon, {});
  const clearNode = trailingIcon ?? (currentValue.length > 0 ? /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: "ctl-ti__clear",
      onClick: handleClear,
      "aria-label": clearLabel,
      disabled,
      children: /* @__PURE__ */ jsx(ClearIcon, {})
    }
  ) : null);
  return /* @__PURE__ */ jsx(
    TextInputCore,
    {
      ...rest,
      type: "search",
      leadingIcon: resolvedLeading,
      trailingSlot: clearNode,
      value: isControlled ? value : void 0,
      defaultValue: !isControlled ? defaultValue : void 0,
      onChange: handleChange,
      disabled,
      ref
    }
  );
});
var LIB_STAMP10 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass9(...parts) {
  return parts.filter(Boolean).join(" ");
}
function resolveLineHeight(el) {
  const cs = window.getComputedStyle(el);
  const raw = cs.lineHeight;
  if (raw && raw.endsWith("px")) {
    const n = parseFloat(raw);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  const fontPx = parseFloat(cs.fontSize) || 14;
  return fontPx * 1.4;
}
function resolveVerticalPadding(el) {
  const cs = window.getComputedStyle(el);
  const pt = parseFloat(cs.paddingTop) || 0;
  const pb = parseFloat(cs.paddingBottom) || 0;
  const bt = parseFloat(cs.borderTopWidth) || 0;
  const bb = parseFloat(cs.borderBottomWidth) || 0;
  return pt + pb + bt + bb;
}
var TextArea = forwardRef(function TextArea2(props, ref) {
  const {
    size = "md",
    variant = "outline",
    autoGrow = true,
    minRows = 3,
    maxRows = Number.POSITIVE_INFINITY,
    invalid = false,
    showCount = false,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    disabled = false,
    readOnly = false,
    className,
    maxLength,
    rows,
    ...rest
  } = props;
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current, []);
  const warnedRef = useRef(false);
  if (!warnedRef.current && value !== void 0 && defaultValue !== void 0) {
    warnedRef.current = true;
    warn({
      fn: "TextArea",
      code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
      message: "TextArea received BOTH `value` (controlled) AND `defaultValue` (uncontrolled). The controlled `value` wins; drop `defaultValue` to silence this warning.",
      received: { value, defaultValue },
      dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@TextArea"
    });
  }
  const [focused, setFocused] = useState(false);
  const [usedLen, setUsedLen] = useState(() => {
    if (typeof value === "string") return value.length;
    if (typeof defaultValue === "string") return defaultValue.length;
    return 0;
  });
  const measureAndApply = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;
    el.style.height = "auto";
    const lh = resolveLineHeight(el);
    const vpad = resolveVerticalPadding(el);
    const minH = lh * Math.max(1, minRows) + vpad;
    const maxH = Number.isFinite(maxRows) ? lh * maxRows + vpad : Number.POSITIVE_INFINITY;
    const sh = el.scrollHeight;
    const next = Math.min(Math.max(sh, minH), maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = sh > maxH ? "auto" : "hidden";
  }, [autoGrow, minRows, maxRows]);
  useLayoutEffect(() => {
    measureAndApply();
  }, [measureAndApply, value, defaultValue]);
  useEffect(() => {
    const el = innerRef.current;
    if (!el || !autoGrow || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        measureAndApply();
      });
    });
    ro.observe(el);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [autoGrow, measureAndApply]);
  const handleChange = (e) => {
    setUsedLen(e.target.value.length);
    measureAndApply();
    onChange?.(e.target.value, e);
  };
  const handleFocus = (e) => {
    setFocused(true);
    onFocus?.(e);
  };
  const handleBlur = (e) => {
    setFocused(false);
    onBlur?.(e);
  };
  const classes = joinClass9(
    "ctl-ta",
    `ctl-ta--size-${size}`,
    `ctl-ta--variant-${variant}`,
    invalid && "ctl-ta--invalid",
    focused && "ctl-ta--focused",
    disabled && "ctl-ta--disabled",
    readOnly && "ctl-ta--readonly",
    autoGrow && "ctl-ta--auto-grow",
    className
  );
  const fieldProps = {
    ...rest,
    className: "ctl-ta__field",
    disabled,
    readOnly,
    maxLength,
    // Honour an explicit `rows` prop when consumer overrides; otherwise
    // fall back to `minRows` so the rendered baseline matches the
    // post-measure height even before JS runs.
    rows: rows ?? Math.max(1, minRows),
    "aria-invalid": invalid || void 0,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur
  };
  if (value !== void 0) {
    fieldProps.value = value;
  } else if (defaultValue !== void 0) {
    fieldProps.defaultValue = defaultValue;
  }
  const renderCount = showCount && typeof maxLength === "number";
  return /* @__PURE__ */ jsxs("div", { className: classes, "data-norkron-lib": LIB_STAMP10, children: [
    /* @__PURE__ */ jsx("textarea", { ref: innerRef, ...fieldProps }),
    renderCount ? /* @__PURE__ */ jsxs("span", { className: "ctl-ta__count", "aria-hidden": "true", children: [
      usedLen,
      "/",
      maxLength
    ] }) : null
  ] });
});
var LIB_STAMP11 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass10(...parts) {
  return parts.filter(Boolean).join(" ");
}
function clampValue(n, min, max) {
  let v = n;
  if (typeof min === "number" && v < min) v = min;
  if (typeof max === "number" && v > max) v = max;
  return v;
}
function roundToPrecision(n, precision) {
  if (typeof precision !== "number" || precision < 0) return n;
  const f = Math.pow(10, precision);
  return Math.round(n * f) / f;
}
function formatNumber(n, precision) {
  if (typeof precision === "number" && precision >= 0) return n.toFixed(precision);
  return String(n);
}
function keyMultiplier(e) {
  if (e.metaKey || e.ctrlKey) return 100;
  if (e.shiftKey) return 10;
  return 1;
}
function useHoldRepeat(action) {
  const timeoutRef = useRef(null);
  const startedAtRef = useRef(0);
  const actionRef = useRef(action);
  actionRef.current = action;
  const tick = useCallback(() => {
    actionRef.current();
    const elapsed = Date.now() - startedAtRef.current;
    const t = Math.min(1, elapsed / 1e3);
    const interval = 300 - (300 - 60) * t;
    timeoutRef.current = setTimeout(tick, interval);
  }, []);
  const start = useCallback(() => {
    if (timeoutRef.current) return;
    startedAtRef.current = Date.now();
    actionRef.current();
    timeoutRef.current = setTimeout(tick, 400);
  }, [tick]);
  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  useEffect(() => stop, [stop]);
  return { start, stop };
}
var MinusIcon = () => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx(
  "line",
  {
    x1: "3",
    y1: "8",
    x2: "13",
    y2: "8",
    stroke: "currentColor",
    strokeWidth: "1.75",
    strokeLinecap: "round"
  }
) });
var PlusIcon = () => /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 16 16", "aria-hidden": "true", focusable: "false", children: [
  /* @__PURE__ */ jsx(
    "line",
    {
      x1: "3",
      y1: "8",
      x2: "13",
      y2: "8",
      stroke: "currentColor",
      strokeWidth: "1.75",
      strokeLinecap: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "line",
    {
      x1: "8",
      y1: "3",
      x2: "8",
      y2: "13",
      stroke: "currentColor",
      strokeWidth: "1.75",
      strokeLinecap: "round"
    }
  )
] });
var ChevronUpIcon = () => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 10 6", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx(
  "polyline",
  {
    points: "1,5 5,1 9,5",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var ChevronDownIcon = () => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 10 6", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx(
  "polyline",
  {
    points: "1,1 5,5 9,1",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.5",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var NumberInput = forwardRef(function NumberInput2(props, ref) {
  const {
    value,
    defaultValue,
    onChange,
    min,
    max,
    step = 1,
    precision,
    prefix,
    suffix,
    size = "md",
    variant = "outline",
    invalid = false,
    disabled = false,
    readOnly = false,
    showSteppers = true,
    clamp: clamp3 = true,
    allowExpression = false,
    className,
    onKeyDown,
    onFocus,
    onBlur,
    ...rest
  } = props;
  const isControlled = value !== void 0;
  const [uncontrolled, setUncontrolled] = useState(
    defaultValue ?? 0
  );
  const current = isControlled ? value : uncontrolled;
  const [draft, setDraft] = useState(
    () => formatNumber(current, precision)
  );
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => inputRef.current);
  useEffect(() => {
    if (!focused) setDraft(formatNumber(current, precision));
  }, [current, precision, focused]);
  useEffect(() => {
    const clamped = clampValue(current, min, max);
    if (clamped !== current) {
      warn({
        fn: "NumberInput",
        code: "WEB_CONTROLS_SLIDER_OUT_OF_RANGE",
        message: `NumberInput received value ${current} outside [${min ?? "-\u221E"}, ${max ?? "+\u221E"}]. Clamping to ${clamped}.`,
        received: current,
        dedupKey: `WEB_CONTROLS_SLIDER_OUT_OF_RANGE@NumberInput:${current}`
      });
      if (!isControlled) setUncontrolled(clamped);
      onChange?.(clamped);
    }
  }, [current, min, max]);
  useEffect(() => {
    if (allowExpression) {
      warn({
        fn: "NumberInput",
        code: "WEB_CONTROLS_INVALID_INPUT",
        message: "NumberInput allowExpression=true is not implemented in v1 \u2014 input is treated as plain numeric. TODO: integrate a safe expression evaluator.",
        received: "allowExpression=true",
        dedupKey: "WEB_CONTROLS_INVALID_INPUT@NumberInput:allowExpression"
      });
    }
  }, [allowExpression]);
  const commit = useCallback(
    (next) => {
      let n = next;
      if (Number.isNaN(n)) return;
      n = roundToPrecision(n, precision);
      if (clamp3) n = clampValue(n, min, max);
      if (!isControlled) setUncontrolled(n);
      onChange?.(n);
      return n;
    },
    [clamp3, isControlled, max, min, onChange, precision]
  );
  const adjust = useCallback(
    (delta) => {
      const base = Number.isFinite(current) ? current : 0;
      commit(base + delta);
    },
    [commit, current]
  );
  const upRepeat = useHoldRepeat(() => adjust(step));
  const downRepeat = useHoldRepeat(() => adjust(-step));
  const handleKeyDown = useCallback(
    (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented || disabled || readOnly) return;
      const mult = keyMultiplier(e);
      if (e.key === "ArrowUp") {
        e.preventDefault();
        adjust(step * mult);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        adjust(-step * mult);
      } else if (e.key === "PageUp") {
        e.preventDefault();
        adjust(step * 10);
      } else if (e.key === "PageDown") {
        e.preventDefault();
        adjust(-step * 10);
      } else if (e.key === "Enter") {
        const parsed = parseFloat(draft);
        if (!Number.isNaN(parsed)) commit(parsed);
      }
    },
    [adjust, commit, disabled, draft, onKeyDown, readOnly, step]
  );
  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value;
      setDraft(raw);
      if (raw === "" || raw === "-" || raw === "." || raw === "-.") return;
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed)) return;
      let n = parsed;
      if (clamp3) n = clampValue(n, min, max);
      if (!isControlled) setUncontrolled(n);
      onChange?.(n);
    },
    [clamp3, isControlled, max, min, onChange]
  );
  const handleFocus = useCallback(
    (e) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );
  const handleBlur = useCallback(
    (e) => {
      setFocused(false);
      const parsed = parseFloat(draft);
      const committed = Number.isNaN(parsed) ? current : commit(parsed) ?? current;
      setDraft(formatNumber(committed, precision));
      onBlur?.(e);
    },
    [commit, current, draft, onBlur, precision]
  );
  const containerClasses = joinClass10(
    "ctl-ni",
    `ctl-ni--size-${size}`,
    `ctl-ni--variant-${variant}`,
    invalid && "ctl-ni--invalid",
    focused && "ctl-ni--focused",
    disabled && "ctl-ni--disabled",
    readOnly && "ctl-ni--readonly",
    showSteppers && "ctl-ni--has-steppers",
    className
  );
  const atMin = typeof min === "number" && current <= min;
  const atMax = typeof max === "number" && current >= max;
  const stopUp = useCallback(() => upRepeat.stop(), [upRepeat]);
  const stopDown = useCallback(() => downRepeat.stop(), [downRepeat]);
  const onUpPointerDown = useCallback(
    (e) => {
      if (disabled || readOnly || atMax) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      upRepeat.start();
    },
    [atMax, disabled, readOnly, upRepeat]
  );
  const onDownPointerDown = useCallback(
    (e) => {
      if (disabled || readOnly || atMin) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      downRepeat.start();
    },
    [atMin, disabled, readOnly, downRepeat]
  );
  return /* @__PURE__ */ jsxs("div", { className: containerClasses, "data-norkron-lib": LIB_STAMP11, children: [
    prefix !== void 0 && prefix !== null && prefix !== "" ? /* @__PURE__ */ jsx("span", { className: "ctl-ni__affix ctl-ni__affix--prefix", "aria-hidden": "true", children: prefix }) : null,
    /* @__PURE__ */ jsx(
      "input",
      {
        ...rest,
        ref: inputRef,
        type: "text",
        inputMode: "decimal",
        className: "ctl-ni__field",
        value: draft,
        disabled,
        readOnly,
        "aria-invalid": invalid || void 0,
        role: "spinbutton",
        "aria-valuenow": Number.isFinite(current) ? current : void 0,
        "aria-valuemin": min,
        "aria-valuemax": max,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        onBlur: handleBlur
      }
    ),
    suffix !== void 0 && suffix !== null && suffix !== "" ? /* @__PURE__ */ jsx("span", { className: "ctl-ni__affix ctl-ni__affix--suffix", "aria-hidden": "true", children: suffix }) : null,
    showSteppers ? /* @__PURE__ */ jsxs("span", { className: "ctl-ni__steppers", "aria-hidden": "true", children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          tabIndex: -1,
          className: "ctl-ni__step ctl-ni__step--up",
          disabled: disabled || readOnly || atMax,
          onPointerDown: onUpPointerDown,
          onPointerUp: stopUp,
          onPointerLeave: stopUp,
          onPointerCancel: stopUp,
          children: /* @__PURE__ */ jsx(ChevronUpIcon, {})
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          tabIndex: -1,
          className: "ctl-ni__step ctl-ni__step--down",
          disabled: disabled || readOnly || atMin,
          onPointerDown: onDownPointerDown,
          onPointerUp: stopDown,
          onPointerLeave: stopDown,
          onPointerCancel: stopDown,
          children: /* @__PURE__ */ jsx(ChevronDownIcon, {})
        }
      )
    ] }) : null
  ] });
});
var Stepper = forwardRef(function Stepper2(props, ref) {
  const {
    value,
    defaultValue,
    onChange,
    min,
    max,
    step = 1,
    size = "md",
    formatValue,
    disabled = false,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy
  } = props;
  const generatedId = useId();
  const valueId = `${generatedId}-value`;
  const isControlled = value !== void 0;
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? 0);
  const current = isControlled ? value : uncontrolled;
  useEffect(() => {
    if (!ariaLabel && !ariaLabelledBy) {
      warn({
        fn: "Stepper",
        code: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL",
        message: 'Stepper rendered without `aria-label` or `aria-labelledby`. Screen-reader users will hear "button" with no context. Provide an accessible name describing what is being adjusted.',
        received: "no aria-label",
        dedupKey: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL@Stepper"
      });
    }
  }, [ariaLabel, ariaLabelledBy]);
  const commit = useCallback(
    (next) => {
      let n = next;
      if (typeof min === "number" && n < min) n = min;
      if (typeof max === "number" && n > max) n = max;
      if (!isControlled) setUncontrolled(n);
      onChange?.(n);
    },
    [isControlled, max, min, onChange]
  );
  const adjust = useCallback(
    (delta) => {
      const base = Number.isFinite(current) ? current : 0;
      commit(base + delta);
    },
    [commit, current]
  );
  const minusRepeat = useHoldRepeat(() => adjust(-step));
  const plusRepeat = useHoldRepeat(() => adjust(step));
  const atMin = typeof min === "number" && current <= min;
  const atMax = typeof max === "number" && current >= max;
  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      const mult = keyMultiplier(e);
      if (e.key === "ArrowUp" || e.key === "ArrowRight") {
        e.preventDefault();
        adjust(step * mult);
      } else if (e.key === "ArrowDown" || e.key === "ArrowLeft") {
        e.preventDefault();
        adjust(-step * mult);
      } else if (e.key === "PageUp") {
        e.preventDefault();
        adjust(step * 10);
      } else if (e.key === "PageDown") {
        e.preventDefault();
        adjust(-step * 10);
      } else if (e.key === "Home" && typeof min === "number") {
        e.preventDefault();
        commit(min);
      } else if (e.key === "End" && typeof max === "number") {
        e.preventDefault();
        commit(max);
      }
    },
    [adjust, commit, disabled, max, min, step]
  );
  const displayed = useMemo(
    () => formatValue ? formatValue(current) : String(current),
    [formatValue, current]
  );
  const rootClasses = joinClass10(
    "ctl-st",
    `ctl-st--size-${size}`,
    disabled && "ctl-st--disabled",
    className
  );
  const onMinusPointerDown = useCallback(
    (e) => {
      if (disabled || atMin) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      minusRepeat.start();
    },
    [atMin, disabled, minusRepeat]
  );
  const onPlusPointerDown = useCallback(
    (e) => {
      if (disabled || atMax) return;
      e.preventDefault();
      e.target.setPointerCapture?.(e.pointerId);
      plusRepeat.start();
    },
    [atMax, disabled, plusRepeat]
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: rootClasses,
      "data-norkron-lib": LIB_STAMP11,
      role: "group",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      onKeyDown: handleKeyDown,
      tabIndex: disabled ? -1 : 0,
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ctl-st__minus",
            "aria-label": "Decrease",
            "aria-controls": valueId,
            disabled: disabled || atMin,
            tabIndex: -1,
            onPointerDown: onMinusPointerDown,
            onPointerUp: minusRepeat.stop,
            onPointerLeave: minusRepeat.stop,
            onPointerCancel: minusRepeat.stop,
            children: /* @__PURE__ */ jsx("span", { className: "ctl-st__minus-icon", children: /* @__PURE__ */ jsx(MinusIcon, {}) })
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            id: valueId,
            className: "ctl-st__value",
            role: "spinbutton",
            "aria-valuenow": Number.isFinite(current) ? current : void 0,
            "aria-valuemin": min,
            "aria-valuemax": max,
            "aria-valuetext": displayed,
            children: displayed
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "ctl-st__plus",
            "aria-label": "Increase",
            "aria-controls": valueId,
            disabled: disabled || atMax,
            tabIndex: -1,
            onPointerDown: onPlusPointerDown,
            onPointerUp: plusRepeat.stop,
            onPointerLeave: plusRepeat.stop,
            onPointerCancel: plusRepeat.stop,
            children: /* @__PURE__ */ jsx("span", { className: "ctl-st__plus-icon", children: /* @__PURE__ */ jsx(PlusIcon, {}) })
          }
        )
      ]
    }
  );
});
var LIB_STAMP12 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass11(...parts) {
  return parts.filter(Boolean).join(" ");
}
var ToggleGroup = forwardRef(function ToggleGroup2(props, ref) {
  const {
    items,
    size = "md",
    variant = "secondary",
    orientation = "horizontal",
    disabled = false,
    className,
    id: idProp
  } = props;
  const autoId = useId();
  const id = idProp ?? `ctl-tg-${autoId}`;
  const isSingle = props.type === "single";
  const [singleInternal, setSingleInternal] = useState(
    () => isSingle ? props.defaultValue ?? null : null
  );
  const [multiInternal, setMultiInternal] = useState(
    () => !isSingle ? props.defaultValue ?? [] : []
  );
  const isControlled = isSingle ? props.value !== void 0 : props.value !== void 0;
  const singleValue = isSingle ? isControlled ? props.value ?? null : singleInternal : null;
  const multiValue = !isSingle ? isControlled ? props.value ?? [] : multiInternal : [];
  const isPressed = useCallback(
    (v) => isSingle ? singleValue === v : multiValue.includes(v),
    [isSingle, singleValue, multiValue]
  );
  const setSingle = (next) => {
    if (!isControlled) setSingleInternal(next);
    props.onChange?.(next);
  };
  const setMulti = (next) => {
    if (!isControlled) setMultiInternal(next);
    props.onChange?.(
      next
    );
  };
  const handleToggle = (value) => {
    if (isSingle) {
      setSingle(singleValue === value ? null : value);
    } else {
      const set = new Set(multiValue);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      setMulti(Array.from(set));
    }
  };
  const enabledIdx = items.findIndex((it) => !(disabled || it.disabled));
  const pressedIdx = items.findIndex(
    (it) => !(disabled || it.disabled) && isPressed(it.value)
  );
  const initialIdx = pressedIdx >= 0 ? pressedIdx : enabledIdx;
  const [focusIdx, setFocusIdx] = useState(initialIdx);
  const itemRefs = useRef([]);
  const moveFocus = (next) => {
    const len = items.length;
    if (len === 0) return;
    const dir = next > focusIdx || next === 0 ? 1 : -1;
    let i = (next % len + len) % len;
    for (let k = 0; k < len; k++) {
      const item = items[i];
      if (item && !(disabled || item.disabled)) break;
      i = ((i + dir) % len + len) % len;
    }
    setFocusIdx(i);
    itemRefs.current[i]?.focus();
  };
  const handleKeyDown = (e) => {
    const isHorizontal = orientation === "horizontal";
    const nextKey = isHorizontal ? "ArrowRight" : "ArrowDown";
    const prevKey = isHorizontal ? "ArrowLeft" : "ArrowUp";
    if (e.key === nextKey) {
      e.preventDefault();
      moveFocus((focusIdx + 1) % items.length);
    } else if (e.key === prevKey) {
      e.preventDefault();
      moveFocus((focusIdx - 1 + items.length) % items.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      moveFocus(0);
    } else if (e.key === "End") {
      e.preventDefault();
      moveFocus(items.length - 1);
    }
  };
  const groupRole = isSingle ? "radiogroup" : "group";
  const rootCls = joinClass11(
    "ctl-tg",
    `ctl-tg--${orientation}`,
    `ctl-tg--size-${size}`,
    `ctl-tg--variant-${variant}`,
    disabled && "ctl-tg--disabled",
    className
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      id,
      role: groupRole,
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      className: rootCls,
      "data-norkron-lib": LIB_STAMP12,
      onKeyDown: handleKeyDown,
      children: items.map((item, i) => {
        const pressed = isPressed(item.value);
        const itemDisabled = disabled || !!item.disabled;
        const isFocusable = i === focusIdx && !itemDisabled;
        const itemCls = joinClass11(
          "ctl-tg__item",
          pressed && "ctl-tg__item--pressed",
          itemDisabled && "ctl-tg__item--disabled"
        );
        const ariaProps = isSingle ? { role: "radio", "aria-checked": pressed } : { "aria-pressed": pressed };
        return /* @__PURE__ */ jsx(
          "button",
          {
            ref: (el) => {
              itemRefs.current[i] = el;
            },
            type: "button",
            className: itemCls,
            disabled: itemDisabled,
            tabIndex: isFocusable ? 0 : -1,
            "data-value": item.value,
            onClick: () => {
              if (itemDisabled) return;
              setFocusIdx(i);
              handleToggle(item.value);
            },
            onFocus: () => setFocusIdx(i),
            ...ariaProps,
            children: /* @__PURE__ */ jsx("span", { className: "ctl-tg__item-label", "data-allow-edge-text": "true", children: item.label })
          },
          item.value
        );
      })
    }
  );
});
var LIB_STAMP13 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
function joinClass12(...parts) {
  return parts.filter(Boolean).join(" ");
}
function isGroup(item) {
  return typeof item === "object" && item !== null && "options" in item && Array.isArray(item.options);
}
var ChevronIcon = () => /* @__PURE__ */ jsx(
  "svg",
  {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx("path", { d: "m6 9 6 6 6-6" })
  }
);
var Select = forwardRef(function Select2(props, ref) {
  const {
    options,
    value,
    defaultValue,
    onChange,
    size = "md",
    variant = "outline",
    invalid = false,
    loading = false,
    disabled = false,
    placeholder,
    leadingIcon,
    className,
    testId,
    id: idProp,
    ...rest
  } = props;
  const field = useFieldContext();
  const effInvalid = invalid || (field?.invalid ?? false);
  const effDisabled = disabled || loading || (field?.disabled ?? false);
  const fieldDescribedBy = field?.describedById;
  const ariaRequired = field?.required || void 0;
  const warnedRef = useRef(false);
  if (!warnedRef.current && value !== void 0 && defaultValue !== void 0) {
    warnedRef.current = true;
    warn({
      fn: "Select",
      code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
      message: "Select received both `value` and `defaultValue`. The lib picks `value` (controlled). Drop `defaultValue` or drop `value`+`onChange`.",
      received: { hasValue: true, hasDefaultValue: true },
      dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@Select"
    });
  }
  const isControlled = value !== void 0;
  const effDefaultValue = isControlled ? void 0 : defaultValue ?? (placeholder !== void 0 ? "" : void 0);
  const autoId = useId();
  const id = idProp ?? field?.controlId ?? autoId;
  const handleChange = useCallback(
    (e) => {
      onChange?.(e.target.value, e);
    },
    [onChange]
  );
  const containerClass = joinClass12(
    "ctl-sel",
    `ctl-sel--size-${size}`,
    `ctl-sel--variant-${variant}`,
    effInvalid && "ctl-sel--invalid",
    effDisabled && "ctl-sel--disabled",
    loading && "ctl-sel--loading",
    className
  );
  const describedBy = rest["aria-describedby"] ?? fieldDescribedBy;
  return /* @__PURE__ */ jsxs("div", { className: containerClass, "data-norkron-lib": LIB_STAMP13, children: [
    leadingIcon && /* @__PURE__ */ jsx("span", { className: "ctl-sel__leading-icon", "aria-hidden": "true", children: leadingIcon }),
    /* @__PURE__ */ jsxs(
      "select",
      {
        ref,
        id,
        className: "ctl-sel__field",
        value: isControlled ? value : void 0,
        defaultValue: effDefaultValue,
        ...rest,
        disabled: effDisabled,
        "aria-invalid": effInvalid || void 0,
        "aria-required": ariaRequired,
        "aria-busy": loading || void 0,
        "aria-describedby": describedBy,
        "data-testid": testId,
        onChange: handleChange,
        children: [
          placeholder !== void 0 && /* @__PURE__ */ jsx("option", { value: "", disabled: true, hidden: true, children: placeholder }),
          options.map(
            (item, i) => isGroup(item) ? /* @__PURE__ */ jsx(
              "optgroup",
              {
                label: item.label,
                disabled: item.disabled,
                children: item.options.map((opt) => /* @__PURE__ */ jsx(
                  "option",
                  {
                    value: opt.value,
                    disabled: opt.disabled,
                    children: opt.label
                  },
                  opt.value
                ))
              },
              `grp-${i}-${item.label}`
            ) : /* @__PURE__ */ jsx(
              "option",
              {
                value: item.value,
                disabled: item.disabled,
                children: item.label
              },
              item.value
            )
          )
        ]
      }
    ),
    loading ? /* @__PURE__ */ jsx(
      "span",
      {
        className: "ctl-sel__spinner",
        role: "status",
        "aria-label": "Loading"
      }
    ) : /* @__PURE__ */ jsx("span", { className: "ctl-sel__chevron", "aria-hidden": "true", children: /* @__PURE__ */ jsx(ChevronIcon, {}) })
  ] });
});

// src/otp-core.ts
var SEPARATOR_CODE_POINTS = /* @__PURE__ */ new Set([
  9,
  10,
  11,
  12,
  13,
  // tab, LF, VT, FF, CR
  32,
  // space
  160,
  // no-break space
  5760,
  // ogham space mark
  8192,
  8193,
  8194,
  8195,
  8196,
  8197,
  8198,
  8199,
  8200,
  8201,
  8202,
  // en/em/thin/hair spaces
  8239,
  8287,
  12288,
  // narrow no-break, medium math, ideographic space
  8203,
  8204,
  8205,
  65279,
  // zero-width space/non-joiner/joiner/BOM
  45,
  // hyphen-minus
  8208,
  8209,
  8210,
  8211,
  8212,
  8213,
  // Unicode dashes
  8722,
  // minus sign
  65123,
  65293,
  // small / fullwidth hyphen-minus
  95,
  // low line
  46,
  183,
  8226,
  8231,
  12539,
  // dots / bullets / katakana middle dot
  58,
  47,
  124,
  92,
  // colon, slash, pipe, backslash
  39,
  8217
  // apostrophes (some banks group with them)
]);
function isOtpSeparator(ch) {
  const cp = ch.codePointAt(0);
  return cp !== void 0 && SEPARATOR_CODE_POINTS.has(cp);
}
var UNICODE_DECIMAL = /^\p{Nd}$/u;
function foldUnicodeDigit(ch) {
  const folded = ch.normalize("NFKC");
  if (folded.length === 1 && folded >= "0" && folded <= "9") return folded;
  const probe = folded.length === 1 ? folded : ch;
  if (!UNICODE_DECIMAL.test(probe)) return null;
  const cp = probe.codePointAt(0);
  if (cp === void 0) return null;
  if (cp >= 48 && cp <= 57) return String.fromCodePoint(cp);
  let zero = cp;
  for (let step = 0; step < 9; step += 1) {
    const prev = zero - 1;
    if (!UNICODE_DECIMAL.test(String.fromCodePoint(prev))) break;
    zero = prev;
  }
  const value = cp - zero;
  return value >= 0 && value <= 9 ? String(value) : null;
}
var ASCII_LETTER = /^[A-Za-z]$/;
function foldOtpChar(ch, charset) {
  const digit = foldUnicodeDigit(ch);
  if (digit !== null) return digit;
  if (charset !== "alphanumeric") return null;
  const folded = ch.normalize("NFKC");
  if (folded.length === 1 && ASCII_LETTER.test(folded)) return folded;
  return null;
}
function isOtpChar(ch, charset) {
  return foldOtpChar(ch, charset) !== null;
}
function resolveUppercase(opts) {
  const charset = opts.charset ?? "numeric";
  return opts.uppercase ?? charset === "alphanumeric";
}
function resolveLength(length) {
  if (!Number.isFinite(length)) return 1;
  return Math.max(1, Math.floor(length));
}
function normalizeOtpText(raw, opts) {
  const length = resolveLength(opts.length);
  const charset = opts.charset ?? "numeric";
  const upper = resolveUppercase(opts);
  let accepted = "";
  let rejected = 0;
  let separators = 0;
  for (const ch of raw) {
    const folded = foldOtpChar(ch, charset);
    if (folded !== null) {
      accepted += upper ? folded.toUpperCase() : folded;
      continue;
    }
    if (isOtpSeparator(ch)) {
      separators += 1;
      continue;
    }
    rejected += 1;
  }
  return {
    code: accepted.slice(0, length),
    rejected,
    separators,
    truncated: accepted.length > length
  };
}
function otpCells(value, length) {
  const n = resolveLength(length);
  const cells = new Array(n).fill("");
  let i = 0;
  for (const ch of value) {
    if (i >= n) break;
    cells[i] = ch;
    i += 1;
  }
  return cells;
}
function isOtpComplete(value, length) {
  return [...value].length >= resolveLength(length);
}
function clampCaret(index, value, length) {
  const n = resolveLength(length);
  const filled = [...value].length;
  const furthest = Math.min(n - 1, filled);
  const raw = Math.floor(index);
  return Math.min(Math.max(0, Number.isFinite(raw) ? raw : 0), furthest);
}
function writeAt(value, index, ch, length) {
  const n = resolveLength(length);
  const chars = [...value].slice(0, n);
  const at = Math.min(Math.max(0, index), Math.min(chars.length, n - 1));
  if (at < chars.length) chars[at] = ch;
  else chars.push(ch);
  return chars.join("");
}
function removeAt(value, index, length) {
  const chars = [...value].slice(0, resolveLength(length));
  if (index < 0 || index >= chars.length) return chars.join("");
  chars.splice(index, 1);
  return chars.join("");
}
function distributeOtp(current, index, raw, opts) {
  const length = resolveLength(opts.length);
  const { code, rejected, truncated } = normalizeOtpText(raw, opts);
  const from = clampCaret(index, current, length);
  if (code.length === 0) {
    return { value: current, caret: from, accepted: 0, rejected, truncated };
  }
  const start = code.length >= length ? 0 : from;
  let next = current;
  let written = 0;
  for (const ch of code) {
    const slot = start + written;
    if (slot >= length) break;
    next = writeAt(next, slot, ch, length);
    written += 1;
  }
  return {
    value: next,
    caret: clampCaret(start + written, next, length),
    accepted: written,
    rejected,
    truncated: truncated || code.length > length - start
  };
}
var NO_OP = {
  value: null,
  caret: null,
  preventDefault: false,
  rejected: false,
  selectAll: false
};
function result(patch) {
  return { ...NO_OP, ...patch };
}
function reduceOtpKey(input) {
  const length = resolveLength(input.length);
  const charset = input.charset ?? "numeric";
  const upper = resolveUppercase({ charset, uppercase: input.uppercase });
  const index = clampCaret(input.index, input.value, length);
  const { key, modifier = false, readOnly = false } = input;
  const cells = otpCells(input.value, length);
  if (modifier) {
    if (key === "a" || key === "A") return result({ selectAll: true, preventDefault: true });
    return NO_OP;
  }
  switch (key) {
    case "Backspace": {
      if (readOnly) return result({ preventDefault: true });
      const target = cells[index] !== "" ? index : Math.max(0, index - 1);
      const next2 = removeAt(input.value, target, length);
      return result({
        value: next2,
        caret: clampCaret(target, next2, length),
        preventDefault: true
      });
    }
    case "Delete": {
      if (readOnly) return result({ preventDefault: true });
      const next2 = removeAt(input.value, index, length);
      return result({
        value: next2,
        caret: clampCaret(index, next2, length),
        preventDefault: true
      });
    }
    case "ArrowLeft":
      return result({ caret: clampCaret(index - 1, input.value, length), preventDefault: true });
    case "ArrowRight":
      return result({ caret: clampCaret(index + 1, input.value, length), preventDefault: true });
    case "Home":
      return result({ caret: 0, preventDefault: true });
    case "End":
      return result({
        caret: clampCaret(length - 1, input.value, length),
        preventDefault: true
      });
    // Non-printable keys the platform must keep owning.
    case "Tab":
    case "Enter":
    case "Escape":
    case "Shift":
    case "Control":
    case "Alt":
    case "Meta":
    case "CapsLock":
    case "ArrowUp":
    case "ArrowDown":
    case "PageUp":
    case "PageDown":
    // `Process`/`Unidentified` mean an IME is mid-composition — never
    // swallow those or the composed text is lost.
    case "Process":
    case "Unidentified":
      return NO_OP;
  }
  if ([...key].length !== 1) return NO_OP;
  const folded = foldOtpChar(key, charset);
  if (folded === null) {
    if (isOtpSeparator(key)) return result({ preventDefault: true });
    return result({ preventDefault: true, rejected: true });
  }
  if (readOnly) return result({ preventDefault: true });
  const next = writeAt(input.value, index, upper ? folded.toUpperCase() : folded, length);
  return result({
    value: next,
    caret: clampCaret(index + 1, next, length),
    preventDefault: true
  });
}
function defaultOtpCaret(value, length) {
  const n = resolveLength(length);
  const cells = otpCells(value, n);
  for (let i = 0; i < n; i += 1) if (cells[i] === "") return i;
  return n - 1;
}
var LIB_STAMP14 = `${WEB_CONTROLS_LIB}@${LIB_VERSION}`;
var NUMERIC_MESSAGES = {
  complete: "Code complete",
  rejected: "Only digits are allowed",
  truncated: "The pasted code was longer than this field and has been shortened",
  loading: "Verifying code",
  cell: (index, length) => `Digit ${index + 1} of ${length}`
};
var TEXT_MESSAGES = {
  ...NUMERIC_MESSAGES,
  rejected: "Only letters and digits are allowed",
  cell: (index, length) => `Character ${index + 1} of ${length}`
};
var MAX_LENGTH = 32;
function joinClass13(...parts) {
  return parts.filter(Boolean).join(" ");
}
var OtpInput = forwardRef(function OtpInput2(props, ref) {
  const {
    length: lengthProp = 6,
    value,
    defaultValue,
    onChange,
    onComplete,
    disabled = false,
    readOnly = false,
    invalid = false,
    loading = false,
    autoFocus = false,
    inputMode = "numeric",
    charset: charsetProp,
    uppercase: uppercaseProp,
    mask = false,
    placeholderChar,
    groupEvery,
    separator = "\u2013",
    size = "md",
    variant = "outline",
    name,
    label,
    messages: messagesProp,
    testId,
    className,
    onFocus,
    onBlur,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedByProp,
    ...rest
  } = props;
  const length = useMemo(() => {
    const n = Math.floor(lengthProp);
    if (!Number.isFinite(n) || n < 1) return 6;
    return Math.min(n, MAX_LENGTH);
  }, [lengthProp]);
  const charset = charsetProp ?? (inputMode === "text" ? "alphanumeric" : "numeric");
  const uppercase = uppercaseProp ?? charset === "alphanumeric";
  const messages = useMemo(
    () => ({
      ...charset === "alphanumeric" ? TEXT_MESSAGES : NUMERIC_MESSAGES,
      ...messagesProp
    }),
    [charset, messagesProp]
  );
  const normalizeOpts = useMemo(
    () => ({ length, charset, uppercase }),
    [length, charset, uppercase]
  );
  const field = useFieldContext();
  const effInvalid = invalid || (field?.invalid ?? false);
  const effDisabled = disabled || (field?.disabled ?? false);
  const effReadOnly = readOnly || loading || (field?.readOnly ?? false);
  const ariaRequired = field?.required || void 0;
  const autoId = useId();
  const baseId = field?.controlId ?? `ctl-otp-${autoId}`;
  const labelId = `${baseId}__inline-label`;
  const statusId = `${baseId}__status`;
  const isControlled = value !== void 0;
  const [internal, setInternal] = useState(
    () => typeof defaultValue === "string" ? defaultValue.slice(0, length) : ""
  );
  const code = (isControlled ? value : internal) ?? "";
  const cells = useMemo(() => otpCells(code, length), [code, length]);
  const complete = isOtpComplete(code, length);
  const [activeIndex, setActiveIndex] = useState(
    () => defaultOtpCaret(code, length)
  );
  const [focused, setFocused] = useState(false);
  const tabStop = focused ? Math.min(activeIndex, length - 1) : defaultOtpCaret(code, length);
  const cellRefs = useRef([]);
  const pendingFocus = useRef(null);
  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;
    pendingFocus.current = null;
    const el = cellRefs.current[target];
    if (!el) return;
    el.focus();
    el.select();
  });
  const focusCell = useCallback((index) => {
    pendingFocus.current = index;
    setActiveIndex(index);
    const el = cellRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  }, []);
  const ZWSP = "\u200B";
  const [statusParts, setStatusParts] = useState([]);
  const announceSeq = useRef(0);
  const announce = useCallback((message) => {
    announceSeq.current += 1;
    setStatusParts(message === null ? [] : [message]);
  }, []);
  const announceAlso = useCallback((message) => {
    announceSeq.current += 1;
    setStatusParts((prev) => [...prev, message]);
  }, []);
  const statusText = statusParts.join(". ");
  const status = statusText === "" ? "" : announceSeq.current % 2 === 0 ? `${statusText}${ZWSP}` : statusText;
  const completedRef = useRef(null);
  useEffect(() => {
    if (!isOtpComplete(code, length)) {
      completedRef.current = null;
      return;
    }
    if (completedRef.current === code) return;
    completedRef.current = code;
    announceAlso(messages.complete);
    onComplete?.(code);
  }, [code, length, onComplete, announceAlso, messages.complete]);
  const warned = useRef(false);
  useEffect(() => {
    if (warned.current) return;
    warned.current = true;
    if (isControlled && defaultValue !== void 0) {
      warn({
        fn: "OtpInput",
        code: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT",
        message: "OtpInput received both `value` (controlled) and `defaultValue` (uncontrolled). The controlled `value` wins; drop `defaultValue`.",
        received: { hasValue: true, hasDefaultValue: true },
        dedupKey: "WEB_CONTROLS_CONTROLLED_VS_DEFAULT@OtpInput"
      });
    }
    const hasName = !!(ariaLabel || ariaLabelledBy || label || field?.labelId);
    if (!hasName) {
      warn({
        fn: "OtpInput",
        code: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL",
        message: "OtpInput rendered with no accessible name. A screen reader announces six unlabelled edit fields. Wrap it in a <Field> with a <FieldLabel>, or pass `label`, `aria-label`, or `aria-labelledby`.",
        received: { hasVisibleLabel: !!label, hasAriaName: !!(ariaLabel || ariaLabelledBy) },
        dedupKey: "WEB_CONTROLS_ICON_BUTTON_NO_LABEL@OtpInput"
      });
    }
    const requested = Math.floor(lengthProp);
    if (!Number.isFinite(requested) || requested < 1 || requested > MAX_LENGTH) {
      warn({
        fn: "OtpInput",
        code: "WEB_CONTROLS_OTP_INVALID_LENGTH",
        message: `OtpInput received length=${String(lengthProp)}, which is outside 1\u2026${MAX_LENGTH}. Falling back to ${length}.`,
        received: { length: lengthProp, clampedTo: length },
        dedupKey: "WEB_CONTROLS_OTP_INVALID_LENGTH@OtpInput"
      });
    }
  }, [
    isControlled,
    defaultValue,
    ariaLabel,
    ariaLabelledBy,
    label,
    field?.labelId,
    lengthProp,
    length
  ]);
  const commit = useCallback(
    (next) => {
      if (next === code) return;
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [code, isControlled, onChange]
  );
  const composingRef = useRef(false);
  const handleKeyDown = useCallback(
    (index) => (e) => {
      if (composingRef.current || e.nativeEvent.isComposing) return;
      const out = reduceOtpKey({
        value: code,
        index,
        key: e.key,
        length,
        charset,
        uppercase,
        modifier: e.ctrlKey || e.metaKey,
        readOnly: effReadOnly
      });
      if (out.preventDefault) e.preventDefault();
      if (out.selectAll) {
        setActiveIndex(index);
        for (const el of cellRefs.current) {
          if (el && el.value !== "") el.setSelectionRange?.(0, el.value.length);
        }
        return;
      }
      announce(out.rejected ? messages.rejected : null);
      if (out.value !== null) commit(out.value);
      if (out.caret !== null) focusCell(out.caret);
    },
    [
      code,
      length,
      charset,
      uppercase,
      effReadOnly,
      announce,
      messages.rejected,
      commit,
      focusCell
    ]
  );
  const applyText = useCallback(
    (index, text) => {
      const out = distributeOtp(code, index, text, normalizeOpts);
      announce(
        out.truncated ? messages.truncated : out.accepted === 0 && out.rejected > 0 ? messages.rejected : null
      );
      if (out.accepted === 0) return;
      commit(out.value);
      focusCell(out.caret);
    },
    [code, normalizeOpts, announce, messages.truncated, messages.rejected, commit, focusCell]
  );
  const handlePaste = useCallback(
    (index) => (e) => {
      e.preventDefault();
      if (effReadOnly) return;
      const text = e.clipboardData?.getData("text") ?? "";
      applyText(index, text);
    },
    [effReadOnly, applyText]
  );
  const handleChange = useCallback(
    (index) => (e) => {
      if (effReadOnly) return;
      const raw = e.target.value;
      if (raw === "") {
        const out = reduceOtpKey({
          value: code,
          index,
          key: "Delete",
          length,
          charset,
          uppercase
        });
        if (out.value !== null) commit(out.value);
        return;
      }
      applyText(index, raw);
    },
    [effReadOnly, code, length, charset, uppercase, commit, applyText]
  );
  const handleCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);
  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false;
  }, []);
  const handleCellFocus = useCallback(
    (index) => (e) => {
      setFocused(true);
      setActiveIndex(index);
      e.currentTarget.select();
    },
    []
  );
  const handleWrapperFocus = useCallback(
    (e) => {
      const cameFromOutside = !e.currentTarget.contains(
        e.relatedTarget
      );
      if (cameFromOutside) onFocus?.(e);
    },
    [onFocus]
  );
  const handleWrapperBlur = useCallback(
    (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );
  const handleCellMouseDown = useCallback(
    (index) => (e) => {
      const target = defaultOtpCaret(code, length);
      if (index <= target) return;
      e.preventDefault();
      focusCell(target);
    },
    [code, length, focusCell]
  );
  const describedBy = [ariaDescribedByProp ?? field?.describedById, statusId].filter(Boolean).join(" ") || void 0;
  const groupLabelledBy = ariaLabelledBy ?? (label ? labelId : field?.labelId);
  const containerClass = joinClass13(
    "ctl-otp",
    `ctl-otp--size-${size}`,
    `ctl-otp--variant-${variant}`,
    effInvalid && "ctl-otp--invalid",
    effDisabled && "ctl-otp--disabled",
    effReadOnly && "ctl-otp--readonly",
    loading && "ctl-otp--loading",
    complete && "ctl-otp--complete",
    mask && "ctl-otp--masked",
    className
  );
  const smallTarget = size === "xs" || size === "sm";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: containerClass,
      role: "group",
      "aria-label": ariaLabel,
      "aria-labelledby": groupLabelledBy,
      "aria-describedby": describedBy,
      "aria-busy": loading || void 0,
      "aria-disabled": effDisabled || void 0,
      "data-norkron-lib": LIB_STAMP14,
      "data-testid": testId,
      "data-otp-length": length,
      "data-otp-status": statusText || void 0,
      onFocus: handleWrapperFocus,
      onBlur: handleWrapperBlur,
      ...rest,
      children: [
        label ? /* @__PURE__ */ jsx("span", { className: "ctl-otp__label", id: labelId, "data-allow-edge-text": "true", children: label }) : null,
        /* @__PURE__ */ jsxs("span", { className: "ctl-otp__cells", children: [
          cells.map((cell, i) => /* @__PURE__ */ jsxs("span", { className: "ctl-otp__slot", children: [
            groupEvery && groupEvery > 0 && i > 0 && i % groupEvery === 0 ? /* @__PURE__ */ jsx(
              "span",
              {
                className: "ctl-otp__separator",
                "aria-hidden": "true",
                "data-allow-edge-text": "true",
                children: separator
              }
            ) : null,
            /* @__PURE__ */ jsx(
              "input",
              {
                ref: (el) => {
                  cellRefs.current[i] = el;
                  if (i !== 0 || !ref) return;
                  if (typeof ref === "function") ref(el);
                  else ref.current = el;
                },
                id: i === 0 ? baseId : `${baseId}__cell-${i}`,
                className: "ctl-otp__cell",
                type: mask ? "password" : "text",
                autoComplete: "one-time-code",
                inputMode,
                pattern: charset === "numeric" ? "[0-9]*" : "[A-Za-z0-9]*",
                autoCorrect: "off",
                autoCapitalize: uppercase ? "characters" : "off",
                spellCheck: false,
                value: cell,
                placeholder: placeholderChar,
                disabled: effDisabled,
                readOnly: effReadOnly,
                tabIndex: i === tabStop ? 0 : -1,
                autoFocus: autoFocus && i === 0,
                "aria-label": messages.cell(i, length),
                "aria-invalid": effInvalid || void 0,
                "aria-required": ariaRequired,
                "aria-describedby": describedBy,
                "data-testid": testId ? `${testId}-cell-${i}` : void 0,
                "data-otp-index": i,
                ...smallTarget ? { "data-allow-small-target": "true" } : {},
                onKeyDown: handleKeyDown(i),
                onChange: handleChange(i),
                onPaste: handlePaste(i),
                onCompositionStart: handleCompositionStart,
                onCompositionEnd: handleCompositionEnd,
                onFocus: handleCellFocus(i),
                onMouseDown: handleCellMouseDown(i)
              }
            )
          ] }, `otp-slot-${i}`)),
          loading ? /* @__PURE__ */ jsx(
            "span",
            {
              className: "ctl-otp__spinner",
              role: "status",
              "aria-label": messages.loading
            }
          ) : null
        ] }),
        name ? /* @__PURE__ */ jsx("input", { type: "hidden", name, value: code }) : null,
        /* @__PURE__ */ jsx(
          "span",
          {
            className: "ctl-otp__status",
            id: statusId,
            role: "status",
            "aria-live": "polite",
            "aria-atomic": "true",
            children: status
          }
        )
      ]
    }
  );
});
function selfCheck(opts = {}) {
  const { emitWarnings = true, scopeSelector } = opts;
  const findings = [];
  if (typeof document === "undefined") return findings;
  const scope = scopeSelector ? document.querySelector(scopeSelector) ?? document.body : document.body;
  const root = scope.querySelector(
    '[data-norkron-lib^="@norkron/web-controls"]'
  )?.closest('body, [role="main"], main') ?? scope;
  const stampedRoots = Array.from(
    scope.querySelectorAll('[data-norkron-lib^="@norkron/web-controls"]')
  );
  const aggregated = [...checkInteractiveElementVisuallyEmpty(root)];
  for (const stamped of stampedRoots) {
    const helperScope = stamped;
    try {
      aggregated.push(
        ...checkContrast(helperScope),
        ...checkViewportOverflow(helperScope),
        ...checkHeadingHierarchy(helperScope),
        ...checkDuplicateVisibleText(helperScope),
        ...checkTextClipped(helperScope),
        ...checkInlineStyleOnLibStamp(helperScope),
        ...checkUtilityClassOnLibStamp(helperScope)
      );
    } catch {
    }
  }
  for (const f of aggregated) {
    if (f.code === "STR_NEEDS_LAYOUT") continue;
    const finding = {
      code: f.code,
      severity: f.severity === "critical" || f.severity === "major" ? "error" : "warning",
      message: f.message,
      subject: f.selector
    };
    findings.push(finding);
    if (emitWarnings) {
      warn({
        fn: "selfCheck",
        code: "WEB_CONTROLS_INVALID_INPUT",
        message: `[conformance ${f.code}] ${f.message}`,
        received: f.selector ?? "",
        dedupKey: `${f.code}@${f.selector ?? ""}`
      });
    }
  }
  return findings;
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-controls",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    // Grounded in the grep above — no @media (viewport), no @container,
    // no matchMedia (viewport). Controls are fluid + density-token
    // driven, never viewport-tier driven. Honest baseline: 'full' at
    // every tier.
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);

export { Button, ButtonGroup, Checkbox, CheckboxGroup, Field, FieldDescription, FieldError, FieldLabel, IconButton, Knob, LIB_VERSION, NumberInput, OtpInput, PasswordInput, RESPONSIVE_CONTRACT, Radio, RadioGroup, SearchInput, Select, Slider, Stepper, Switch, TextArea, TextInput, ToggleGroup, WEB_CONTROLS_LIB, WEB_CONTROLS_TOKEN, defaultOtpCaret, distributeOtp, foldOtpChar, foldUnicodeDigit, isOtpChar, isOtpComplete, isOtpSeparator, normalizeOtpText, otpCells, panic, reduceOtpKey, reportInvalidInput, selfCheck, useFieldContext, warn };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map